
import metaObjects from "../modules/metaObjects";

const PROTOCOL = 'https://';
const DEFAULT_HOST = window.loadEnv("REACT_APP_LANGIFY_DEFAULT_HOST", process.env.REACT_APP_LANGIFY_DEFAULT_HOST);
const RETURN_URL = window.loadEnv("REACT_APP_LANGIFY_RETURN_URL", process.env.REACT_APP_LANGIFY_RETURN_URL);
const ENDPOINT = '/v2/api/';
const KEY = 'wvSQbHavRa';

let HOST = null;

const DELAY = 1000;
function later(time) {
  return new Promise(function(resolve,reject){
    setTimeout(function(){resolve(time);},time);
  });
};

const handleError = function (err) {
	console.warn(err);
	return new Response(JSON.stringify(err));
};

const sleeper = function (ms) {
	if(ms > 0) { 
        console.info(`A fetch() is delayed for ${ms} milliseconds to reduce load on the Shopify API.`); 
    }
    return function(x) {
        return new Promise(resolve => setTimeout(() => resolve(x), ms));
    };
}

let apiCallLimitWaitDate = new Date();
let apiCallLimitWaitTime = 0;

// A request may declare the context it belongs to, and one whose context is gone is dropped
// instead of answered - the response is what carries another language's text into the one on
// screen. The declaration is per call on purpose: a request that declares nothing is never
// aborted, so the shop, plan, theme list and language calls every screen needs survive a
// switch, without a list of exemptions to keep up to date.
const activeRequests = [];
const removeActiveRequest = (entry) => {
    const index = activeRequests.indexOf(entry);
    if(index !== -1) {
        activeRequests.splice(index, 1);
    }
};
// An abort resolves with a Response-shaped stub instead of rejecting, so the sentinel travels
// the `.then(data => data.json())` chain every method here already has. A rejection would end
// up in their `.catch(error => response(error))` and reach the reducer looking like a failed
// request, which means an error dialog with a retry button for something the merchant did on
// purpose.
const abortedResponse = {
    ok: false,
    aborted: true,
    headers: new Headers(),
    json: () => Promise.resolve({aborted: true}),
};

const _fetch = (url, options, scope) => {
    //console.log(`fetch("${url}")`);
    const nowDate = new Date();
    const defaultOptions = {
        headers: {
            'x-referrer': window.location.href
        }
    }
    if(nowDate.valueOf() > apiCallLimitWaitDate.valueOf()) {
        apiCallLimitWaitTime = 0;
    }
    const controller = new AbortController();
    const entry = {controller: controller, scope: scope || null};
    if(scope) {
        activeRequests.push(entry);
    }
    return fetch(url, {defaultOptions, ...options, signal: controller.signal})
        .then(sleeper(apiCallLimitWaitTime))
        .then((res) => {
            if(res.headers.get('x-langify-api-call-limit')) {
                const apiCallLimitData = JSON.parse(res.headers.get('x-langify-api-call-limit'));
                if(apiCallLimitData && apiCallLimitData.currentlyAvailable < 500) {
                    apiCallLimitWaitTime = ((apiCallLimitData.maximumAvailable - apiCallLimitData.currentlyAvailable) / apiCallLimitData.restoreRate) * 1000;
                    apiCallLimitWaitDate = new Date(Date.now() + apiCallLimitWaitTime)
                }
            }
            return res;
        })
        .catch((error) => {
            if(error && error.name === 'AbortError') {
                return abortedResponse;
            }
            throw error;
        })
        .finally(() => removeActiveRequest(entry));
}

const fetchSequential = (urls, payloads, index = 0, results = [], onChunk = null) => {
	if (index >= urls.length) {
		//console.log('ALL DONE', results);
	  return Promise.resolve(results);
	}
	//console.log(`fetch url: ${urls[index]}`);
	return fetch(urls[index], payloads[index])
	  .then(response => {
			if (!response.ok) {
				throw new Error('Network response error');
			}
			return response.json();
		})
		.then(data => {
			//console.log(`Data from ${urls[index]}:`, data);
			results.push(data);
			if (onChunk) onChunk();
			return fetchSequential(urls, payloads, index + 1, results, onChunk);
		});
};
function chunkArray(array, chunkSize) {
	const chunkedArray = [];
	for (let i = 0; i < array.length; i += chunkSize) {
	  chunkedArray.push(array.slice(i, i + chunkSize));
	}
	return chunkedArray;
}
function chunkObject(object, chunkSize) {
  const keys = Object.keys(object);
  const chunkedObjects = [];
  for (let i = 0; i < keys.length; i += chunkSize) {
    const chunkedObject = {};
    const currentKeys = keys.slice(i, i + chunkSize);
    currentKeys.forEach(key => {
      chunkedObject[key] = object[key];
    });
    chunkedObjects.push(chunkedObject);
  }
  return chunkedObjects;
}

const handleResponse = async (response) => {
  try {
    const data = await response.json();
    return data;
  } catch (err) {
    return { success: false, message: 'Invalid JSON response' };
  }
};


const API = {
    bulkOperationTimeouts: {},
    // set by ImportReviewDialog: shows an import file's warnings, resolves true to import anyway
    reviewImportFile: null,
    // No call sites since _fetch took over: every request now registers in `activeRequests`
    // with the scope it declared. Kept rather than deleted so a one-off that declares no scope
    // can still be made abortable without rebuilding this.
    activeControllers: [],
  
    async fetchAbortable(url, options = {}) {
      //debugger;
      const controller = new AbortController();
      const { signal } = controller;
  
      this.activeControllers.push(controller);
  
      try {
        const response = await fetch(url, { ...options, signal });
        if (!response.ok) {
          throw new Error(`Error while loading data: ${response.status}`);
        }
        return response;
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted:', url);
        } else {
          console.error('Fetch-Error:', error);
        }
        throw error;
      } finally {
        this.removeController(controller);
      }
    },

    removeController(controller) {
      //console.log('Removed abort controller');
      this.activeControllers = this.activeControllers.filter((c) => c !== controller);
    },

    // "A machine translation is running": the window of the translate request, which is charged
    // server-side and cannot be aborted. It opens with the request and closes with its answer;
    // the save that follows is an ordinary scoped save with its own warning. The expiry is the
    // backstop for an answer that never comes.
    protectedRunUntil: 0,

    beginProtectedRun() {
      API.protectedRunUntil = Date.now() + 180000;
    },

    endProtectedRun() {
      API.protectedRunUntil = 0;
    },

    // "A machine translation is running" for the switch warning: the translate request only.
    isProtectedRunActive() {
      return API.protectedRunUntil > Date.now();
    },

    // The translate request cannot be aborted - it is a plain fetch and the words are spent
    // server-side before the client sees a string. What can be stopped is the save it would
    // have triggered, so the run is marked cancelled and its response writes nothing.
    runToken: 0,

    cancelProtectedRun() {
      API.protectedRunUntil = 0;
      // Bumped even with no window up: a run still counting words, or one that outlived the
      // backstop, is reachable only through the token. A spare bump cancels nothing.
      API.runToken += 1;
    },

    // Aborts the requests declared for the context being left. A request that declared no
    // scope, or one for another language or theme, keeps running. So does one declared
    // `held`: the bulk translation tool's syncs carry the run's own language and sections, so
    // the merchant's selector says nothing about them - but only them, never what else is out.
    abortScope(scope) {
      // Only a language switch invalidates the run here: the translation is paid for a
      // language, not a theme. On the theme routes, where a theme switch does interrupt it,
      // the switch cancels explicitly - and only there was the merchant warned.
      if(scope.language != undefined) {
        API.cancelProtectedRun();
      }
      const keys = Object.keys(scope);
      activeRequests
        .filter((entry) => entry.scope && !entry.scope.held && keys.every((key) =>
          entry.scope[key] != undefined && entry.scope[key] == scope[key]))
        .forEach((entry) => entry.controller.abort());
    },

    clearAllFetches() {
      API.cancelProtectedRun();
      //console.log('Abbrechen aller laufenden Fetches...');
      // Leaving the section drops what was declared for it as well, not only the count and
      // sync calls that were registered here before.
      const held = activeRequests.filter((entry) => entry.scope && entry.scope.held);
      activeRequests.splice(0).filter((entry) => !held.includes(entry)).forEach((entry) => entry.controller.abort());
      activeRequests.push(...held);
    	if(this.activeControllers.length === 0) return false;
      this.activeControllers.forEach((controller) => controller.abort());
      this.activeControllers = [];
    },

    clearHost: () => {
        localStorage.removeItem('langify_host');

        return true;
    },
    changeHost: (host) => {
        localStorage.setItem('langify_host', host);

        return true;
    },
    startSession: (response) => {
        API.generateToken(data => {
            if(data.success === true && data.token && data.api_host) {
                let usedHost = data.api_host;

                // locally changed hosts win over the ones returned by the server
                if(localStorage.getItem('langify_host')) {
                    usedHost = localStorage.getItem('langify_host');
                }

                if(usedHost == DEFAULT_HOST) {
                    HOST = DEFAULT_HOST;
                    response({success:true});
                } else {
                    HOST = usedHost;

                    API.initiateSession(data.token, data => {
                        if(data.success === true) {

                            if(data.deeplink_uri) {
                              fetch(data.deeplink_uri, {
                                method: 'GET',
                                headers: {
                                  "Accept": "application/json"
                                },
                                credentials: 'include'
                              })
                                .then(response => {
                                  if (!response.ok) {
                                    throw new Error(`HTTP error: ${response.status}`);
                                  }
                                  return response.json();
                                })
                                .then(json => {
                                  data.nextPath = json.nextPath;
                                  response(data);
                                })
                                .catch(err => console.error("Fehler:", err));

                            } else {
                              response(data);
                            }

                        } else {
                            API.logout(result => {
                                if(result.success === true && result.redirect_uri) {
                                    window.location.replace(result.redirect_uri)
                                }
                            });
                        }
                    });
                }
            } else {
                if(window.location.hostname === 'v2.langify-app.com') {
                    window.location.replace('https://langify-app.com')
                } else if(window.location.hostname === 'localhost' || window.location.hostname === 'v2.langify.local') {
                    window.location.replace('https://langify.local')
                } else if(window.location.hostname === 'langify.lovely-app.com') {
                    window.location.replace('https://langify.herokuapp.com')
                }
            }
        });
    },
    generateToken: (response) => {
        fetch(PROTOCOL + DEFAULT_HOST + ENDPOINT + 'token/generate?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            response(data)
        })
        .catch(error => response(error));
    },
    initiateSession: (token, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'session/initiate?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + token
            }
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    newLogout: (response) => {
                console.log('newLogout')
        fetch(PROTOCOL + HOST + ENDPOINT + 'shop/logout?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    getShop: (response) => {
        _fetch(PROTOCOL + HOST + ENDPOINT + 'shop' + '?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            if(data.version < 3) {
                if(window.location.hostname === 'v2.langify-app.com') {
                    window.location.replace('https://langify-app.com')
                } else if(window.location.hostname === 'localhost' || window.location.hostname === 'v2.langify.local') {
                    window.location.replace('https://langify.local')
                } else if(window.location.hostname === 'langify.lovely-app.com') {
                    window.location.replace('https://langify.herokuapp.com')
                }
            }
            if(data.success === false && data.redirect_url) {
                window.location.replace(data.redirect_url)
            }
            response(data);
        })
        .catch(() => {
            if(window.location.hostname === 'v2.langify-app.com') {
                window.location.replace('https://langify-app.com')
            } else if(window.location.hostname === 'localhost' || window.location.hostname === 'v2.langify.local') {
                window.location.replace('https://langify.local')
            } else if(window.location.hostname === 'langify.lovely-app.com') {
                window.location.replace('https://langify.herokuapp.com')
            }
        })
    },
    setEditLanguage: (language, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'shop/edit_language/' + language + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch();
    },
    oldLogout: (response) => {
                console.log('oldLogout')
        fetch(PROTOCOL + DEFAULT_HOST + ENDPOINT + 'shop/logout?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    logout: (response) => {
        if(HOST === DEFAULT_HOST) {
            API.oldLogout(result => {
                response(result);
            });
        } else {
            API.newLogout(newResult => {
                console.log(newResult)
                API.oldLogout(oldResult => {
                    console.log(oldResult)

                    // Cookie für langify-app.com löschen
                    document.cookie = "PHPSESSID=; path=/; domain=langify-app.com; expires=Thu, 01 Jan 1970 00:00:00 UTC";

                    // Cookie für api.langify-app.com löschen
                    document.cookie = "PHPSESSID=; path=/; domain=api.langify-app.com; expires=Thu, 01 Jan 1970 00:00:00 UTC";

                    response(newResult);
                })
            })
        }
    },
    getShopProgress: (language, progress, response) => {
        progress(0)
        _fetch(PROTOCOL + HOST + ENDPOINT + 'shop/progress/' + language + '?key=' + KEY,
        {
            credentials: 'include'
        }, {language: language})
        .then(data => data.json())
        .then(data => {
            if(data.aborted === true) {
                response(data);
                return;
            }
            progress(100)
            response(data);
        })
        .catch();
    },
    syncShopMetafields: (language, progress, response) => {
        let ids = [];
        let numPages = 0;
        let numAllPages = 0;
        const perPage = 50;
        const allPerPage = 250;
        const scope = {language: language};
        let stopped = false;

        _fetch(PROTOCOL + HOST + ENDPOINT + 'shop/metafields/count/' + language + '?key=' + KEY,
            {
                credentials: 'include'
            }, scope)
            .then(data => data.json())
            .then(async data => {
                if(data.aborted === true) {
                    stopped = true;
                    response(data);
                    return data;
                }
                // sync/end runs on whatever `ids` holds, so a failed count must not fall through
                // to it - the section would be replaced with the result of an empty id list.
                if(data.success !== true) {
                    stopped = true;
                    response(data);
                    return data;
                }

                numPages = Math.ceil(data.count / perPage)
                numAllPages = Math.ceil(data.all_count / allPerPage)

                // The ids pass carries no language in its url, but it belongs to this run all the
                // same - its ids are what sync/end writes for `language`. Declaring the scope
                // here rather than deriving it from the url is what makes that possible.
                return API.__paginateItems('shop/metafields/ids', null, 1, numAllPages, allPerPage, null, (data, currentProgress) => {
                    progress(currentProgress);
                    ids = ids.concat(data.ids)
                }, false, null, null, null, scope);
            })
            .then(paged => {
                if(stopped) {
                    return null;
                }
                if(paged && paged.aborted === true) {
                    stopped = true;
                    response(paged);
                    return null;
                }

                return API.__paginateItems('shop/metafields/sync/start', language, 1, numPages, perPage, null, (data, currentProgress) => {
                    progress(currentProgress);
                    ids = ids.concat(data.ids)
                }, false, null, null, null, scope)
            })
            .then(paged => {
                if(stopped) {
                    return null;
                }
                if(paged && paged.aborted === true) {
                    stopped = true;
                    response(paged);
                    return null;
                }

                return _fetch(PROTOCOL + HOST + ENDPOINT + 'shop/metafields/sync/end/' + language + '?key=' + KEY,
                    {
                        credentials: 'include',
                        method: 'POST',
                        body: JSON.stringify(ids)
                    }, scope)
            })
            .then(data =>  data ? data.json() : null)
            .then(data => {
                if(stopped || data === null) {
                    return;
                }
                if(data.aborted === true) {
                    response(data)
                    return;
                }

                progress(100)
                response(data)
            })
            .catch(error => response(error))

    },
    saveShopMetafields: async (language, items, progress, response) => {
        let fetches = []
        // the backend spends one Shopify mutation per changed item, nothing for the rest
        let perCall = 10;
        let calls = Math.ceil(items.length / perCall)

        for(let i = 0; i < calls; ++i) {
            let usedItems = []
            for(let j = 0; j < perCall; ++j) {
                const index = (i * perCall) + j;
                if(index < items.length) {
                    usedItems.push(items[index])
                }
            }


            const chunk = await _fetch(PROTOCOL + HOST + ENDPOINT + 'shop/metafields/save/' + language + '?key=' + KEY,
                    {
                        credentials: 'include',
                        body: JSON.stringify(usedItems),
                        method: 'POST'
                    }, {language: language})
                .then(data => data.json())
                .catch(e => e)

            // Five metafields per request, so aborting the one in flight does not end the loop.
            if(chunk && chunk.aborted === true) {
                response(chunk);
                return;
            }

            fetches.push(chunk)
        }

        Promise.all(fetches)
        .then(data => {
            response(data)
        })
        .catch(error => response(error));
    },
    getShopSubscription: (response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'shop/subscription?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => { response(data); })
        .catch(error => response(error));
    },  
    syncProductMetafields: (product, language, progress, response) => {
        const scope = {language: language};
        let ids = [];
        let syncids = [];
        let numPages = 0;
        let numAllPages = 0;
        const perPage = 50;
        const allPerPage = 50;

        _fetch(PROTOCOL + HOST + ENDPOINT + 'product/metafields/count/' + product + '?key=' + KEY,
            {
                credentials: 'include'
            }, scope)
            .then(data => data.json())
            .then(API.__bailIfAborted)
            .then(async data => {
                if(data.success === true) {
                    return API.__paginateItems('product/metafields/ids/' + product, null, 1, null, allPerPage, null, (data, currentProgress) => {
                        ids = ids.concat(data.ids);
                        progress({
                            value: ids.length,
                            type: 'fields_found'
                        });
                    }, false, undefined, undefined, undefined, scope)
                }
            })
            .then(API.__bailIfAborted)
            .then(data => {
                return API.__paginateItems('product/metafields/sync/start/' + product, language, 1, null, perPage, null, (data, currentProgress) => {
                    syncids = syncids.concat(data.ids)
                    progress({
                        value: syncids.length,
                        type: 'fields_loaded'
                    });
                }, false, undefined, undefined, undefined, scope)
            })
            .then(API.__bailIfAborted)
            .then(data => {
                return _fetch(PROTOCOL + HOST + ENDPOINT + 'product/metafields/sync/end/' + product + '/' + language + '?key=' + KEY,
                    {
                        credentials: 'include',
                        method: 'POST',
                        body: JSON.stringify(ids)
                    }, scope)
            })
            .then(data =>  data.json())
            .then(API.__bailIfAborted)
            .then(data => {
                response(data)
            })
            .catch(error => response(error))
    },
    syncProductVariantMetafields: async (product, language, progress, response) => {
        let metafields = [];
        let totalItems = 0;
        let processedItems = 0;
    
        try {
            const fetchData = await _fetch(PROTOCOL + HOST + ENDPOINT + 'product_variants/' + product + '?key=' + KEY, {
                credentials: 'include'
            }, {language: language});
            const data = await fetchData.json();
            if(data && data.aborted === true) {
                response(data);
                return;
            }
    
            totalItems = data.variants.length;
            let aborted = false;
    
            // Every variant is a chain of its own, so a dropped one has to end the loop as well
            for (let i = 0; i < totalItems && !aborted; i++) {
                //console.log(i, data.variants[i]);
    
                await API.__syncProductVariantMetafields(
                    data.variants[i],
                    language,
                    tmpProgress => {},
                    tmpData => {
                        if(tmpData && tmpData.aborted === true) {
                            aborted = true;
                            response(tmpData);
                            return;
                        }
                        processedItems++;
    
                        progress(Math.round((processedItems / totalItems) * 100));
    
                        if (tmpData.success === true) {
                            metafields = metafields.concat(tmpData.metafields);
                        }
    
                        // Fortschritt melden
                        if (processedItems === totalItems) {
                            progress(100);
                            response({
                                success: true,
                                product_id: product,
                                variant_metafields: metafields
                            });
                        }
                    }
                );
            }
        } catch (error) {
            response({ success: false, error });
        }
    },    
    __syncProductVariantMetafields: async (productVariant, language, progress, response) => {
        const scope = {language: language};
        let ids = [];
        let numPages = 0;
        let numAllPages = 0;
        const perPage = 50;
        const allPerPage = 250;

        await _fetch(PROTOCOL + HOST + ENDPOINT + 'product_variant/metafields/count/' + productVariant + '?key=' + KEY,
            {
                credentials: 'include'
            }, scope)
            .then(data => data.json())
            .then(API.__bailIfAborted)
            .then(async data => {
                if(data.success === true) {
                    numPages = Math.ceil(data.count / perPage)
                    numAllPages = Math.ceil(data.all_count / allPerPage)

                    return API.__paginateItems('product_variant/metafields/ids/' + productVariant, null, 1, numAllPages, allPerPage, null, (data, currentProgress) => {
                        progress(currentProgress);
                        ids = ids.concat(data.ids)
                    }, false, undefined, undefined, undefined, scope)
                }
            })
            .then(API.__bailIfAborted)
            .then(data => {
                return API.__paginateItems('product_variant/metafields/sync/start/' + productVariant, language, 1, numPages, perPage, null, (data, currentProgress) => {
                    progress(currentProgress);
                    ids = ids.concat(data.ids)
                }, false, undefined, undefined, undefined, scope)
            })
            .then(API.__bailIfAborted)
            .then(data => {
                return _fetch(PROTOCOL + HOST + ENDPOINT + 'product_variant/metafields/sync/end/' + productVariant + '/' + language + '?key=' + KEY,
                    {
                        credentials: 'include',
                        method: 'POST',
                        body: JSON.stringify(ids)
                    }, scope)
            })
            .then(data =>  data.json())
            .then(API.__bailIfAborted)
            .then(data => {
                progress(100)
                response(data)
            })
            .catch(error => response(error))
    },
    syncPageMetafields: (page, language, progress, response) => {
        const scope = {language: language};
        let ids = [];
        let numPages = 0;
        let numAllPages = 0;
        const perPage = 50;
        const allPerPage = 250;

        _fetch(PROTOCOL + HOST + ENDPOINT + 'page/metafields/count/' + page + '?key=' + KEY,
            {
                credentials: 'include'
            }, scope)
            .then(data => data.json())
            .then(API.__bailIfAborted)
            .then(async data => {
                if(data.success === true) {
                    numPages = Math.ceil(data.count / perPage)
                    numAllPages = Math.ceil(data.all_count / allPerPage)

                    return API.__paginateItems('page/metafields/ids/' + page, null, 1, numAllPages, allPerPage, null, (data, currentProgress) => {
                        progress(currentProgress);
                        ids = ids.concat(data.ids)
                    }, false, undefined, undefined, undefined, scope)
                }
            })
            .then(API.__bailIfAborted)
            .then(data => {
                return API.__paginateItems('page/metafields/sync/start/' + page, language, 1, numPages, perPage, null, (data, currentProgress) => {
                    progress(currentProgress);
                    ids = ids.concat(data.ids)
                }, false, undefined, undefined, undefined, scope)
            })
            .then(API.__bailIfAborted)
            .then(data => {
                return _fetch(PROTOCOL + HOST + ENDPOINT + 'page/metafields/sync/end/' + page + '/' + language + '?key=' + KEY,
                    {
                        credentials: 'include',
                        method: 'POST',
                        body: JSON.stringify(ids)
                    }, scope)
            })
            .then(data =>  data.json())
            .then(API.__bailIfAborted)
            .then(data => {
                progress(100)
                response(data)
            })
            .catch(error => response(error))

    },
    syncCollectionMetafields: (collection, language, progress, response) => {
        const scope = {language: language};
        let ids = [];
        const perPage = 50;
        const allPerPage = 250;

        _fetch(PROTOCOL + HOST + ENDPOINT + 'collection/metafields/count/' + collection + '?key=' + KEY,
            {
                credentials: 'include'
            }, scope)
            .then(data => data.json())
            .then(API.__bailIfAborted)
            .then(async data => {
                if(data.success === true) {
                    return API.__paginateItems('collection/metafields/ids/' + collection, null, 1, null, allPerPage, null, (data, currentProgress) => {
                        progress(currentProgress);
                        ids = ids.concat(data.ids)
                    }, false, undefined, undefined, undefined, scope)
                }
            })
            .then(API.__bailIfAborted)
            .then(data => {
                return API.__paginateItems('collection/metafields/sync/start/' + collection, language, 1, null, perPage, null, (data, currentProgress) => {
                    progress(currentProgress);
                    ids = ids.concat(data.ids)
                }, false, undefined, undefined, undefined, scope)
            })
            .then(API.__bailIfAborted)
            .then(data => {
                return _fetch(PROTOCOL + HOST + ENDPOINT + 'collection/metafields/sync/end/' + collection + '/' + language + '?key=' + KEY,
                    {
                        credentials: 'include',
                        method: 'POST',
                        body: JSON.stringify(ids)
                    }, scope)
            })
            .then(data =>  data.json())
            .then(API.__bailIfAborted)
            .then(data => {
                progress(100)
                response(data)
            })
            .catch(error => response(error))

    },
    syncBlogMetafields: (blog, language, progress, response) => {
        const scope = {language: language};
        let ids = [];
        let numPages = 0;
        let numAllPages = 0;
        const perPage = 50;
        const allPerPage = 250;

        _fetch(PROTOCOL + HOST + ENDPOINT + 'blog/metafields/count/' + blog + '?key=' + KEY,
            {
                credentials: 'include'
            }, scope)
            .then(data => data.json())
            .then(API.__bailIfAborted)
            .then(async data => {
                if(data.success === true) {
                    numPages = Math.ceil(data.count / perPage)
                    numAllPages = Math.ceil(data.all_count / allPerPage)

                    return API.__paginateItems('blog/metafields/ids/' + blog, null, 1, numAllPages, allPerPage, null, (data, currentProgress) => {
                        progress(currentProgress);
                        ids = ids.concat(data.ids)
                    }, false, undefined, undefined, undefined, scope)
                }
            })
            .then(API.__bailIfAborted)
            .then(data => {
                return API.__paginateItems('blog/metafields/sync/start/' + blog, language, 1, numPages, perPage, null, (data, currentProgress) => {
                    progress(currentProgress);
                    ids = ids.concat(data.ids)
                }, false, undefined, undefined, undefined, scope)
            })
            .then(API.__bailIfAborted)
            .then(data => {
                return _fetch(PROTOCOL + HOST + ENDPOINT + 'blog/metafields/sync/end/' + blog + '/' + language + '?key=' + KEY,
                    {
                        credentials: 'include',
                        method: 'POST',
                        body: JSON.stringify(ids)
                    }, scope)
            })
            .then(data =>  data.json())
            .then(API.__bailIfAborted)
            .then(data => {
                progress(100)
                response(data)
            })
            .catch(error => response(error))

    },
    syncArticleMetafields: (article, language, progress, response) => {
        const scope = {language: language};
        let ids = [];
        let numPages = 0;
        let numAllPages = 0;
        const perPage = 50;
        const allPerPage = 250;

        _fetch(PROTOCOL + HOST + ENDPOINT + 'article/metafields/count/' + article + '?key=' + KEY,
            {
                credentials: 'include'
            }, scope)
            .then(data => data.json())
            .then(API.__bailIfAborted)
            .then(async data => {
                if(data.success === true) {
                    numPages = Math.ceil(data.count / perPage)
                    numAllPages = Math.ceil(data.all_count / allPerPage)

                    return API.__paginateItems('article/metafields/ids/' + article, null, 1, numAllPages, allPerPage, null, (data, currentProgress) => {
                        progress(currentProgress);
                        ids = ids.concat(data.ids)
                    }, false, undefined, undefined, undefined, scope)
                }
            })
            .then(API.__bailIfAborted)
            .then(data => {
                return API.__paginateItems('article/metafields/sync/start/' + article, language, 1, numPages, perPage, null, (data, currentProgress) => {
                    progress(currentProgress);
                    ids = ids.concat(data.ids)
                }, false, undefined, undefined, undefined, scope)
            })
            .then(API.__bailIfAborted)
            .then(data => {
                return _fetch(PROTOCOL + HOST + ENDPOINT + 'article/metafields/sync/end/' + article + '/' + language + '?key=' + KEY,
                    {
                        credentials: 'include',
                        method: 'POST',
                        body: JSON.stringify(ids)
                    }, scope)
            })
            .then(data =>  data.json())
            .then(API.__bailIfAborted)
            .then(data => {
                progress(100)
                response(data)
            })
            .catch(error => response(error))

    },
    getLanguages: (response) => {
      _fetch(PROTOCOL + HOST + ENDPOINT + 'languages' + '?key=' + KEY,
      {
          credentials: 'include'
      })
      .then(data => data.json())
      .then(data => {
          response(data);
      })
      // without an answer the app stayed on its loading screen
      .catch(error => response({ success: false, error: error?.message }));
    },
    createLanguage: (languageData, progress, response) => {
        progress(0)
        fetch(PROTOCOL + HOST + ENDPOINT + 'languages/create?key=' + KEY,
        {
            credentials: 'include',
            body: JSON.stringify(languageData),
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            progress(100)
            response(data)
        })
        .catch(error => response({ success: false, error: error?.message }))

    },
    saveLanguage: (language, languageData, progress, response) => {
        progress(0)
        fetch(PROTOCOL + HOST + ENDPOINT + 'languages/save/' + language + '?key=' + KEY,
        {
            credentials: 'include',
            body: JSON.stringify(languageData),
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            progress(100)
            response(data)
        })
        .catch(error => response({ success: false, error: error?.message }))
    },
    restoreLanguage: (language, progress, response) => {
        progress(0)
        fetch(PROTOCOL + HOST + ENDPOINT + 'languages/restore/' + language + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            progress(100)
            response(data)
        })
        .catch(error => response({ success: false, error: error?.message }))
    },
    deleteLanguage: (language, progress, response) => {
        progress(0)
        fetch(PROTOCOL + HOST + ENDPOINT + 'languages/delete/' + language + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            progress(100)
            response(data)
        })
        .catch(error => response({ success: false, error: error?.message }))
    },
    publishLanguage: (language, progress, response) => {
        progress(0)
        fetch(PROTOCOL + HOST + ENDPOINT + 'languages/publish/' + language + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            progress(100)
            response(data)
        })
        .catch(error => response({ success: false, error: error?.message }))
    },
    unpublishLanguage: (language, progress, response) => {
        progress(0)
        fetch(PROTOCOL + HOST + ENDPOINT + 'languages/unpublish/' + language + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            progress(100)
            response(data)
        })
        .catch(error => response({ success: false, error: error?.message }))
    },
    getThemes: (response) => {
        _fetch(PROTOCOL + HOST + ENDPOINT + 'themes' + '?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
      })
      .catch();
    },
    publishTranslations: (themeId, progress, response) => {
      later(DELAY * 3)
      .then(() => {
        response({success: true, themeId: themeId})
      })
    },
    unpublishTranslations: (themeId, progress, response) => {
      later(DELAY * 3)
      .then(() => {
        response({success: true, themeId: themeId})
      })
    },
    refreshTranslations: (themeId, progress, response) => {
      later(DELAY * 6)
      .then(() => {
        response({success: true, progress, themeId: themeId})
      })
    },
    getCustomContents: (language, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'custom_contents/get/' + language + '?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
      })
      .catch(error => response(error));
    },
    saveCustomContents: async (language, items, progress, response) => {
        let i = 0;
        let j = items.length
        let fetches = []
        for(let i = 0; i < j; i++) {
            fetches.push(
                await _fetch(PROTOCOL + HOST + ENDPOINT + 'custom_contents/save/' + language + '?key=' + KEY,
                {
                    credentials: 'include',
                    body: JSON.stringify(items[i]),
                    method: 'POST'
                })
                .then(data => data.json())
                .catch(e => e)
            )
        }

        Promise.all(fetches)
        .then(data => {
            response(data)
        })
        .catch(error => response(error))
    },
    createCustomContents: async (language, items, progress, response) => {
        let i = 0;
        let j = items.length
        let fetches = []
        for(let i = 0; i < j; i++) {
            fetches.push(
                await _fetch(PROTOCOL + HOST + ENDPOINT + 'custom_contents/create/' + language + '?key=' + KEY,
                {
                    credentials: 'include',
                    body: JSON.stringify(items[i]),
                    method: 'POST'
                })
                .then(data => data.json())
                .catch(e => e)
            )
        }

        Promise.all(fetches)
        .then(data => {
            response(data)
        })
        .catch(error => response(error))
    },
    deleteCustomContents: async (ids, progress, response) => {
        let i = 0;
        let j = ids.length
        let fetches = []
        for(let i = 0; i < j; i++) {
            fetches.push(
                await _fetch(PROTOCOL + HOST + ENDPOINT + 'custom_contents/delete/' + ids[i] + '?key=' + KEY,
                {
                    credentials: 'include',
                    method: 'POST'
                })
                .then(data => data.json())
                .catch(e => e)
            )
        }

        Promise.all(fetches)
        .then(data => {
            response(data)
        })
        .catch(error => response(error))
    },
    syncPages: (language, progress, response, sync_reset, options = {}) => {
        let ids = [];
        let datastring = null;
        const scope = {language: language, held: options.held === true};
        let stopped = false;

        _fetch(PROTOCOL + HOST + ENDPOINT + 'pages/count/' + language + '?key=' + KEY,
            {
                credentials: 'include',
            }, scope)
            .then(data => data.json())
            .then(async data => {
                if(data.aborted === true) {
                    stopped = true;
                    response(data);
                    return data;
                }
                // sync/end runs on whatever `ids` holds, so a failed count must not fall through
                // to it - the section would be replaced with the result of an empty id list.
                if(data.success !== true) {
                    stopped = true;
                    response(data);
                    return data;
                }

                const count = data.count
                const perPage = 50
                const numPages = Math.ceil(count / perPage)
                return API.__paginateItems('pages/sync/start', language, 1, numPages, perPage, null, (data, currentProgress) => {
                    progress(currentProgress);
                    ids = ids.concat(data.ids)
                }, sync_reset, null, null, null, scope)
            })
            .then(paged => {
                if(stopped) {
                    return null;
                }
                if(paged && paged.aborted === true) {
                    stopped = true;
                    response(paged);
                    return null;
                }

                return _fetch(PROTOCOL + HOST + ENDPOINT + 'pages/sync/end/' + language + '?key=' + KEY,
                    {
                        credentials: 'include',
                        method: 'POST',
                        body: JSON.stringify(ids)
                    }, scope)
            })
            .then(data =>  data ? data.json() : null)
            .then(data => {
                if(stopped || data === null) {
                    return;
                }
                if(data.aborted === true) {
                    response(data)
                    return;
                }

                progress(100)
                response(data)
            })
            .catch(error => response(error));
    },
    syncPage: (pageId, languageId, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'page/sync/' + pageId + '/' + languageId + '?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    savePages: async (language, items, force, progress, response) => {
        let i = 0;
        let j = items.length
        let fetches = []
        let errors = []
        for(let i = 0; i < j; i++) {
            const item = await _fetch(PROTOCOL + HOST + ENDPOINT + 'page/save/' + language + '/' + force + '?key=' + KEY,
                {
                    credentials: 'include',
                    body: JSON.stringify(items[i]),
                    method: 'POST'
                }, {language: language})
                .then(data => data.json())
                .catch(e => e)

            // One page per request, so aborting the one in flight does not end the loop.
            if(item && item.aborted === true) {
                response(item);
                return;
            }

            fetches.push(item)
        }

        Promise.all(fetches)
        .then(data => response(data))
        .catch(error => response(error));
    },
    // `scope` is passed by the callers that want their pages dropped when the context is left.
    // It is not derived from `language`, because the same paginator serves the exports and the
    // metafield syncs, and each of those chains has to bail on the sentinel itself before it can
    // be gated - an unchecked chain would finalise with an empty id list.
    // A stage that answers the abort sentinel ends the chain: the next stage would otherwise
    // issue fresh requests for the context just left, which no abort can reach any more.
    __bailIfAborted: (data) => {
        if(data && data.aborted === true) throw data;
        return data;
    },
    __paginateItems: async (apiAction, language, page, numPages, perPage, pageLink, callback, sync_reset, options={}, usedPageLinks=new Set(), countData, scope) => {
        //console.log({apiAction, language, page, numPages, perPage, pageLink, callback, sync_reset, options});
        //debugger;
        //console.log(countData)

        // Safety check: If numPages is known, detect if we've exceeded expected pages
        if(typeof numPages === 'number' &&
            numPages > 0 &&
            page > numPages * 1.1 &&
            (!countData || countData.precision !== 'AT_LEAST')
        ) {
            console.warn(`__paginateItems: Page ${page} exceeds expected numPages ${numPages} - stopping pagination, returning items loaded so far`);
            return Promise.resolve({});
        }

        // Detect duplicate page links to prevent infinite loops
        if(pageLink && usedPageLinks.has(pageLink)) {
            console.warn(`__paginateItems: Duplicate pageLink detected "${pageLink}" - stopping pagination, returning items loaded so far`);
            return Promise.resolve({});
        }
        if(pageLink) {
            usedPageLinks.add(pageLink);
        }

        let bodyObj = {
            'page_link': pageLink,
        };
        bodyObj = {...bodyObj, ...options};
        if(sync_reset === true) {
            bodyObj.sync_reset = sync_reset;
        }
        if(numPages !== null && numPages <= 0 && !sync_reset) {
            return new Promise((resolve, reject) => {resolve({})})
        }

        return await _fetch(PROTOCOL + HOST + ENDPOINT + apiAction + '/' + page + '/' + perPage + (language ? '/' + language : '') + '?key=' + KEY,
            {
                credentials: 'include',
                body: JSON.stringify(bodyObj),
                method: 'POST'
            }, scope)
        .then(data => data.json())
        .then(data => {
            // A dropped page ends the pagination and travels up. Handing the sentinel to the
            // callback would concatenate it into the section's id list, and the stage after the
            // pagination would then finalise the sync with that list.
            if(data.aborted === true) {
                return data;
            }

            let progress = (numPages > 0) ?
                Math.round(page / (numPages) * 100)
            :
                {
                    perPage,
                    page,
                }
            ;

            callback(data, progress)
            if(data.next_page_link !== null) {
                return API.__paginateItems(apiAction, language, page + 1, numPages, perPage, data.next_page_link, callback, sync_reset, options, usedPageLinks || new Set(), countData, scope)
            }

            return data;
        })
    },
    // `scope` is passed by the caller, as in __paginateItems: an undeclared page is never aborted.
    __paginateNavigation: async (apiAction, language, perPage, linkCursor, menuCursor, callback, scope) => {
        return await _fetch(PROTOCOL + HOST + ENDPOINT + apiAction + '/' + perPage + (language ? '/' + language : '') + '?key=' + KEY,
            {
                credentials: 'include',
                body: JSON.stringify({'link_cursor': linkCursor, 'menu_cursor': menuCursor}),
                method: 'POST'
            }, scope)
        .then(data => data.json())
        .then(data => {
            // A dropped page ends the walk and travels up. The sentinel carries no page_info, so
            // reading the cursors off it would throw before the caller ever sees it.
            if(data.aborted === true) {
                return data;
            }

            callback(data)
            if((data.page_info_links.has_next_page && data.page_info_links.end_cursor) || (data.page_info_menus.has_next_page && data.page_info_menus.end_cursor)) {
                return API.__paginateNavigation(apiAction, language, perPage, data.page_info_links.end_cursor, data.page_info_menus.end_cursor, callback, scope)
            }

            return data;
        })
    },
    // every section import starts with this upload; anything that goes wrong ends in fail(), the section waits for its callback
    __uploadImportFile: (action, file, onUploaded, fail, cancel) => {
        const xhr = new XMLHttpRequest();
        xhr.withCredentials = true;
        xhr.onerror = () => fail();
        xhr.onload = () => {
            let response = null;
            try {
                response = JSON.parse(xhr.response);
            } catch (e) {}
            // the dialog names the file, the merchant may not remember which one they picked
            const fileName = file instanceof FormData ? file.get('file')?.name : file?.name;
            // a refused file is explained in the review dialog, not as an error to try again
            if(response && response.code === 'FILE_NOT_UTF8' && API.reviewImportFile) {
                API.reviewImportFile({ error: response.code, fileName }).then(() => cancel());
                return;
            }
            if(xhr.status !== 200 || !response || response.success != true) {
                fail(response && (response.message || response.error));
                return;
            }
            // only a file with suspicious rows waits for the merchant, a clean one is imported right away
            const warnings = (response.import_check && response.import_check.warnings) || [];
            const review = warnings.length > 0 && API.reviewImportFile
                ? API.reviewImportFile({ ...response.import_check, fileName })
                : Promise.resolve(true);
            review
                .then(confirmed => confirmed ? onUploaded(response) : cancel())
                .catch(error => fail(error && error.message));
        };
        xhr.open('POST', PROTOCOL + HOST + ENDPOINT + action + '?key=' + KEY, true);
        xhr.send(file);
    },
    // the sections whose reducer takes the list of page results; a failure becomes an entry of that list
    __importListInPages: (uploadAction, file, buildPages, progress, callback) => {
        const failedPage = message => ({ success: false, message: message || 'Import failed' });
        API.__uploadImportFile(uploadAction, file, async response => {
            const pages = buildPages(response);
            const results = [];
            for(let i = 0; i < pages.length; i++) {
                // pages are independent slices, so a failed one doesn't stop the others
                results.push(await _fetch(pages[i].url,
                    {
                        credentials: 'include',
                        method: 'POST',
                        body: JSON.stringify(pages[i].body)
                    })
                    .then(data => data.json())
                    .catch(error => failedPage(error && error.message)));
                progress(Math.round((i + 1) / pages.length * 100));
            }
            progress(100);
            callback(results);
        }, message => callback([failedPage(message)]), () => callback({ cancelled: true }));
    },
    __importTranslations: async (apiAction, language, url, page, numPages, perPage, translations, map, nextLine, importIdentifier, callback, progress, payloadData={}) => {
        return await _fetch(PROTOCOL + HOST + ENDPOINT + apiAction + '/' + perPage + '/' + language + '?key=' + KEY,
            {
                credentials: 'include',
                body: JSON.stringify({
                    url: url, 
                    translations: translations, 
                    map: map, 
                    next_line: nextLine,
                    import_identifier: importIdentifier,
                    ...payloadData
                }),
                method: 'POST'
            })
        .then(data => data.json())
        .then(data => {
            // the next page would start from this one's next_line
            if(data.success !== true) {
                return data;
            }
            callback(data)
            if(page < numPages) {
                progress(Math.round(page / numPages * 100));
                return API.__importTranslations(apiAction, language, url, page + 1, numPages, perPage, translations, data.map, data.next_line, importIdentifier, callback, progress, payloadData)
            }

            return data;
        })
    },
    syncProducts: (language, progress, batchCallback, response, sync_reset, options = {}) => {
        let ids = [];
        let tags = [];
        let types = [];
        let vendors = [];
        const scope = {language: language, held: options.held === true};
        let stopped = false;

        _fetch(PROTOCOL + HOST + ENDPOINT + 'products/count/' + language + '?key=' + KEY,
            {
                credentials: 'include',
            }, scope)
            .then(data => data.json())
            .then(async data => {
                if(data.aborted === true) {
                    stopped = true;
                    response(data);
                    return data;
                }
                // Without this the chain walks on to sync/end with an empty id list and the
                // reducer replaces the section with nothing. It used to be unreachable because
                // fetchAbortable threw on a failed status; through _fetch a failed count with a
                // json body arrives here instead.
                if(data.success !== true) {
                    stopped = true;
                    response(data);
                    return data;
                }
                if(data.success === true) {
                    const count = data.count
                    const perPage = 70
                    const numPages = Math.ceil(count / perPage)

                    return API.__paginateItems('products/sync/start', language, 1, numPages, perPage, null, (data, currentProgress) => {
                        const pageIds = data.ids
                        ids = ids.concat(pageIds)
                        tags = tags.concat(data.tags)
                        types = types.concat(data.types)
                        vendors = vendors.concat(data.vendors)

                        progress(currentProgress, ids.length, count);

                        // Produktdaten für diese Seite sofort laden (non-blocking)
                        // fetch(PROTOCOL + HOST + ENDPOINT + 'products/sync/batch/' + language + '?key=' + KEY, {
                        //     credentials: 'include',
                        //     method: 'POST',
                        //     body: JSON.stringify({ ids: pageIds })
                        // })
                        // .then(r => r.json())
                        // .then(d => { if (d.products && d.products.length > 0) batchCallback(d.products) })
                    }, sync_reset, null, null, data, scope)
                }
            })
            .then(async (paged) => {
                // The stages below finalise the sync with whatever `ids` holds, so a dropped
                // page has to end it here. Letting it through would replace the section with
                // the result of an empty id list.
                if(stopped) {
                    return;
                }
                if(paged && paged.aborted === true) {
                    response(paged);
                    return;
                }

                tags = tags.filter((item, pos) => {return tags.indexOf(item) == pos})
                types = types.filter((item, pos) => {return types.indexOf(item) == pos})
                vendors = vendors.filter((item, pos) => {return vendors.indexOf(item) == pos})

                const syncEndBody = (filter) => JSON.stringify({ ids, filter, tags, types, vendors })
                const syncEndUrl = PROTOCOL + HOST + ENDPOINT + 'products/sync/end/' + language + '?key=' + KEY
                const syncEndOpts = { credentials: 'include', method: 'POST' }

                // Produkte via sync/end finalisieren (bereits per Batch angezeigt)
                const productsData = await _fetch(syncEndUrl, { ...syncEndOpts, body: syncEndBody('products') }, scope)
                    .then(r => r.json())

                if(productsData.aborted === true) {
                    response(productsData);
                    return;
                }

                // Metadaten parallel laden
                const metaResults = await Promise.all(['tags', 'types', 'vendors'].map(filter =>
                    _fetch(syncEndUrl, { ...syncEndOpts, body: syncEndBody(filter) }, scope)
                        .then(r => r.json())
                        .then(d => ({ key: filter, value: d[filter], aborted: d.aborted === true }))
                ))

                if(metaResults.some(item => item.aborted)) {
                    response({aborted: true});
                    return;
                }

                const newData = { products: productsData.products, success: true }
                metaResults.forEach(item => { newData[item.key] = item.value })
                progress(100)
                response(newData)
            })
            .catch(error => response(error))

    },
    resyncProducts: (language, progress, batchCallback, response) => {
        _fetch(PROTOCOL + HOST + ENDPOINT + 'products/reset/sync/' + language + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST'
        }, {language: language})
        .then(data => data.json())
        .then(data => {
            if(data.success == true) {
                API.syncProducts(language, progress, batchCallback, response)
            } else {
                response(data)
            }
        })
        .catch(error => response(error));
    },
    syncProduct: (productId, languageId, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'product/sync/' + productId + '/' + languageId + '?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    saveProducts: async (language, items, force, progress, response) => {
        let i = 0;
        let j = items.length
        let fetches = []
        for(let i = 0; i < j; i++) {
            const item = await _fetch(PROTOCOL + HOST + ENDPOINT + 'product/save/' + language  + '/' + force + '?key=' + KEY,
                {
                    credentials: 'include',
                    body: JSON.stringify(items[i]),
                    method: 'POST'
                }, {language: language})
                .then(data => {
                    progress(Math.round(i / j * 100))
                    return data.json()
                })
                .catch(e => e)

            // One product per request, so aborting the one in flight does not end the loop.
            if(item && item.aborted === true) {
                response(item);
                return;
            }

            fetches.push(item)
        }

        Promise.allSettled([
            ...fetches, 
            //...[Promise.reject(new Error("an error"))]
        ])
        .then(data => {
            progress(100)
            response(data)
        })
        .catch(error => response(error))
    },
    resaveProducts: async (language, items, progress, response) => {
        let i = 0;
        let j = items.length
        let fetches = []
        for(let i = 0; i < j; i++) {
            const item = await _fetch(PROTOCOL + HOST + ENDPOINT + 'product/resave/' + items[i] + '/' + language + '?key=' + KEY,
                {
                    credentials: 'include',
                    body: JSON.stringify(items[i]),
                    method: 'POST'
                }, {language: language})
                .then(data => {
                    progress(Math.round(i / j * 100))
                    return data.json()
                })
                .catch(e => e)

            if(item && item.aborted === true) {
                response(item);
                return;
            }

            fetches.push(item)
        }

        Promise.all(fetches)
        .then(data => {
            progress(100)
            response(data)
        })
        .catch(error => response(error))
    },
    saveProductsMetadata: async (language, tags, types, vendors, force, progress, response) => {
        let fetches = []

        if(tags.length > 0) {
            const tagsCount = tags.length
            const tagsPerPage = 50
            const tagsNumPages = Math.ceil(tagsCount / tagsPerPage)

            for(let i = 0; i < tagsNumPages; i++) {
                let tagItems = [];

                for(let j = 0; j < tagsPerPage; j++) {
                    let index = (i * tagsPerPage) + j;
                    if(index < tags.length) {
                        tagItems.push({...tags[index], type:'tags'})
                    }
                }

                const chunk = await _fetch(PROTOCOL + HOST + ENDPOINT + 'product/save_metadata/' + language + '/' + force + '?key=' + KEY,
                    {
                        credentials: 'include',
                        body: JSON.stringify(tagItems),
                        method: 'POST'
                    }, {language: language})
                    .then(data => data.json())
                    .catch(e => e)

                // 50 entries per request, so aborting the one in flight does not end the loop.
                if(chunk && chunk.aborted === true) {
                    response(chunk);
                    return;
                }

                fetches.push(chunk)
            }
        }

        if(types.length > 0) {
            const typesCount = types.length
            const typesPerPage = 50
            const typesNumPages = Math.ceil(typesCount / typesPerPage)

            for(let i = 0; i < typesNumPages; i++) {
                let typeItems = [];

                for(let j = 0; j < typesPerPage; j++) {
                    let index = (i * typesPerPage) + j;
                    if(index < types.length) {
                        typeItems.push({...types[index], type:'types'})
                    }
                }

                const chunk = await _fetch(PROTOCOL + HOST + ENDPOINT + 'product/save_metadata/' + language + '/' + force + '?key=' + KEY,
                    {
                        credentials: 'include',
                        body: JSON.stringify(typeItems),
                        method: 'POST'
                    }, {language: language})
                    .then(data => data.json())
                    .catch(e => e)

                if(chunk && chunk.aborted === true) {
                    response(chunk);
                    return;
                }

                fetches.push(chunk)
            }
        }

        if(vendors.length > 0) {
            const vendorsCount = vendors.length
            const vendorsPerPage = 50
            const vendorsNumPages = Math.ceil(vendorsCount / vendorsPerPage)

            for(let i = 0; i < vendorsNumPages; i++) {
                let vendorItems = [];

                for(let j = 0; j < vendorsPerPage; j++) {
                    let index = (i * vendorsPerPage) + j;
                    if(index < vendors.length) {
                        vendorItems.push({...vendors[index], type:'vendors'})
                    }
                }

                const chunk = await _fetch(PROTOCOL + HOST + ENDPOINT + 'product/save_metadata/' + language + '/' + force + '?key=' + KEY,
                    {
                        credentials: 'include',
                        body: JSON.stringify(vendorItems),
                        method: 'POST'
                    }, {language: language})
                    .then(data => data.json())
                    .catch(e => e)

                if(chunk && chunk.aborted === true) {
                    response(chunk);
                    return;
                }

                fetches.push(chunk)
            }
        }

        Promise.all(fetches)
        .then(data => {
            response(data)
        })
        .catch(error => response(error))
    },
    getProductOptionValuesPaginated: (productId, languageId, page, response) => {
        let perPage = 100;
        // /v2/api/product_option_values/get/{productId}/{languageId}/{page}/{perPage}
        fetch(PROTOCOL + HOST + ENDPOINT + 'product_option_values/get/' + productId + '/' + languageId + '/' + page + '/' + perPage + '?key=' + KEY,
            {
                credentials: 'include'
            })
            .then(data => data.json())
            .then(data => {
                response(data);
            })
        .catch(error => response(error));
    },
    saveProductOptionValues: (languageId, options, response) => {
        // /v2/api/product_option_values/save/{languageId}
        fetch(PROTOCOL + HOST + ENDPOINT + 'product_option_values/save/' + languageId + '?key=' + KEY,
            {
                credentials: 'include',
                method: 'POST',
                body: JSON.stringify({
                    options: options
                })
            })
            .then(data => data.json())
            .then(data => {
                response(data);
            })
        .catch(error => response(error));
    },
    syncCollections: (language, progress, response, sync_reset, options = {}) => {
        let ids = [];
        let perPage = 50
        let numPages = 0;
        const scope = {language: language, held: options.held === true};
        let stopped = false;

        _fetch(PROTOCOL + HOST + ENDPOINT + 'collections/count/' + language + '?key=' + KEY,
            {
                credentials: 'include',
            }, scope)
            .then(data => data.json())
            .then(async data => {
                if(data.aborted === true) {
                    stopped = true;
                    response(data);
                    return data;
                }
                if(data.success !== true) {
                    stopped = true;
                    response(data);
                    return data;
                }

                numPages = Math.ceil(data.count / perPage)
                return API.__paginateItems('collections/sync/start', language, 1, numPages, perPage, null, (data, currentProgress) => {
                    progress(currentProgress);
                    ids = ids.concat(data.ids)
                }, sync_reset, null, null, null, scope)
            })
            .then(paged => {
                if(stopped) {
                    return null;
                }
                if(paged && paged.aborted === true) {
                    stopped = true;
                    response(paged);
                    return null;
                }

                return _fetch(PROTOCOL + HOST + ENDPOINT + 'collections/sync/end/' + language + '?key=' + KEY,
                    {
                        credentials: 'include',
                        method: 'POST',
                        body: JSON.stringify(ids)
                    }, scope)
            })
            .then(data =>  data ? data.json() : null)
            .then(data => {
                if(stopped || data === null) {
                    return;
                }
                if(data.aborted === true) {
                    response(data)
                    return;
                }

                progress(100)
                response(data)
            })
            .catch(error => response(error));
    },
    syncCollection: (collectionId, languageId, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'collection/sync/' + collectionId + '/' + languageId + '?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    saveCollections: async (language, items, force, progress, response) => {
        let i = 0;
        let j = items.length
        let fetches = []
        for(let i = 0; i < j; i++) {
            const item = await _fetch(PROTOCOL + HOST + ENDPOINT + 'collection/save/' + language + '/' + force + '?key=' + KEY,
                {
                    credentials: 'include',
                    body: JSON.stringify(items[i]),
                    method: 'POST'
                }, {language: language})
                .then(data => data.json())
                .catch(e => e)

            if(item && item.aborted === true) {
                response(item);
                return;
            }

            fetches.push(item)
        }

        Promise.all(fetches)
        .then(data => {
            response(data)
        })
        .catch(error => response(error))
    },
    syncBlogs: (language, progress, response, options = {}) => {
        progress(0)
        _fetch(PROTOCOL + HOST + ENDPOINT + 'blogs/sync/' + language + '?key=' + KEY,
        {
            credentials: 'include'
        }, {language: language, held: options.held === true})
        .then(data => data.json())
        .then(data => {
            if(data.aborted === true) {
                response(data);
                return;
            }
            progress(100)
            response(data);
        })
        .catch(error => response(error));
    },
    syncBlog: (blogId, languageId, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'blog/sync/' + blogId + '/' + languageId + '?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    saveBlogs: async (language, items, force, progress, response) => {
        let i = 0;
        let j = items.length
        let fetches = []
        for(let i = 0; i < j; i++) {
            const item = await _fetch(PROTOCOL + HOST + ENDPOINT + 'blog/save/' + language  + '/' + force + '?key=' + KEY,
                {
                    credentials: 'include',
                    body: JSON.stringify(items[i]),
                    method: 'POST'
                }, {language: language})
                .then(data => data.json())
                .catch(e => e)

            // One blog per request, so aborting the one in flight does not end the loop.
            if(item && item.aborted === true) {
                response(item);
                return;
            }

            fetches.push(item)
        }

        Promise.all(fetches)
        .then(data => {
            response(data)
        })
        .catch(error => response(error))
    },
    syncArticles: async (language, progress, response, sync_reset, options = {}) => {
      let articles = [];
      const scope = {language: language, held: options.held === true};
      try {
        const res = await _fetch(
          PROTOCOL + HOST + ENDPOINT + 'blogs/sync/' + language + '?key=' + KEY,
          { credentials: 'include' }, scope
        );
        const data = await res.json();

        if (data.aborted === true) {
          response(data);
          return;
        }

        // The blogs are walked one after another and the articles of all of them are answered as
        // one list, so a drop anywhere in here has to end the whole run. Carrying on would report
        // `success: true` with the articles of the blogs that finished before the switch, and the
        // reducer would store that subset as the section.
        for (let i = 0; i < data.blogs.length; i++) {
          const item = data.blogs[i];
          let ids = [];

          const countRes = await _fetch(
            PROTOCOL + HOST + ENDPOINT + 'articles/' + item.id + '/count/' + language + '?key=' + KEY,
            { credentials: 'include' }, scope
          );
          const countData = await countRes.json();

          if (countData.aborted === true) {
            response(countData);
            return;
          }

          if (countData.success === true) {
            const count = countData.count;
            const perPage = 50;
            const numPages = Math.ceil(count / perPage);

            const paged = await API.__paginateItems(
              'articles/' + item.id + '/sync/start',
              language,
              1,
              numPages,
              perPage,
              null,
              (data, currentProgress) => {
                progress(currentProgress);
                ids = ids.concat(data.ids);
              },
              sync_reset,
              null,
              null,
              null,
              scope
            );

            if (paged && paged.aborted === true) {
              response(paged);
              return;
            }
          }

          const endRes = await _fetch(
            PROTOCOL + HOST + ENDPOINT + 'articles/' + item.id + '/sync/end/' + language + '?key=' + KEY,
            {
              credentials: 'include',
              method: 'POST',
              body: JSON.stringify(ids)
            }, scope
          );
          const endData = await endRes.json();

          if (endData.aborted === true) {
            response(endData);
            return;
          }

          if (endData.articles) {
            articles = articles.concat(endData.articles);
          }
        }

        response({
          success: true,
          articles: articles
        });

      } catch (error) {
        response(error);
      }
    },
    syncArticle: (articleId, languageId, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'articles/sync/' + articleId + '/' + languageId + '?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    saveArticles: async (language, items, force, progress, response) => {
        let i = 0;
        let j = items.length
        let fetches = []
        for(let i = 0; i < j; i++) {
            const item = await _fetch(PROTOCOL + HOST + ENDPOINT + 'article/save/' + language + '/' + force + '?key=' + KEY,
                {
                    credentials: 'include',
                    body: JSON.stringify(items[i]),
                    method: 'POST'
                }, {language: language})
                .then(data => data.json())
                .catch(e => e)

            // One article per request, so aborting the one in flight does not end the loop.
            if(item && item.aborted === true) {
                response(item);
                return;
            }

            fetches.push(item)
        }

        Promise.all(fetches)
        .then(data => {
            response(data)
        })
        .catch(error => response(error))
    },
    syncNavigation: (language, progress, response, options = {}) => {
        progress(0)
        let ids = [];
        const scope = {language: language, held: options.held === true};

        API.__paginateNavigation('navigation/sync/start', language, 50, null, null, (data) => {
            ids = ids.concat(data.ids);
        }, scope)
        .then(paged => {
            // sync/end writes whatever `ids` holds, so a dropped page has to end the run here.
            if(paged && paged.aborted === true) {
                response(paged);
                return null;
            }

            return _fetch(PROTOCOL + HOST + ENDPOINT + 'navigation/sync/end/' + language + '?key=' + KEY, {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify(ids)
            }, scope);
        })
        .then(res => res ? res.json() : null)
        .then(data => {
            if(data === null) {
                return;
            }
            if(data.aborted === true) {
                response(data);
                return;
            }

            progress(100);
            response(data);
        })
        .catch(error => response(error));
    },
    saveNavigation: async (language, items, progress, response) => {
        let i = 0;
        let j = items.length
        let fetches = []
        for(let i = 0; i < j; i++) {
            const item = await _fetch(PROTOCOL + HOST + ENDPOINT + 'navigation/save/' + language + '?key=' + KEY,
                {
                    credentials: 'include',
                    body: JSON.stringify(items[i]),
                    method: 'POST'
                }, {language: language})
                .then(data => data.json())
                .catch(e => e)

            // One menu per request, so aborting the one in flight does not end the loop.
            if(item && item.aborted === true) {
                response(item);
                return;
            }

            fetches.push(item)
        }

        Promise.all(fetches)
        .then(data => {
            response(data)
        })
        .catch(error => response(error))
    },
    syncStaticTheme: (themeId, languageId, progress, response) => {
        progress(0)
        _fetch(PROTOCOL + HOST + ENDPOINT + 'static_theme/' + themeId + '/sync/' + languageId + '?key=' + KEY,
        {
            credentials: 'include'
        }, {language: languageId, theme: themeId})
        .then(data => data.json())
        .then(data => {
            if(data.aborted === true) {
                response(data);
                return;
            }
            progress(100)
            response(data);
        })
        .catch(error => response(error));
    },
    syncStaticThemeAsset: (themeId, assetId, languageId, progress, response, themeWrite=true) => {
        progress(0)
        _fetch(PROTOCOL + HOST + ENDPOINT + 'static_theme_asset/' + themeId + '/' + assetId + '/sync/' + languageId + '?theme_write=' + themeWrite + '&key=' + KEY,
        {
            credentials: 'include'
        }, {language: languageId, theme: themeId})
        .then(data => data.json())
        .then(data => {
            if(data.aborted === true) {
                response(data);
                return;
            }
            progress(100)
            response(data);
        })
        .catch(error => response(error));
    },
    saveStaticTheme: async (themeId, language, items, progress, response, themeWrite=true) => {
        const perPage = 20
        const numPages = Math.ceil(items.length / perPage)
        let fetches = []
        const prepareAssets = []
        for(let i = 0; i < numPages; i++) {
            let usedItems = [];

            for(let j = 0; j < perPage; j++) {
                let index = (i * perPage) + j;
                if(index < items.length) {
                    if(!prepareAssets.includes(items[index].assetId)) {
                        prepareAssets.push(items[index].assetId)
                    }
                    usedItems.push({...items[index]})
                }
            }

            const chunk = await _fetch(PROTOCOL + HOST + ENDPOINT + 'static_theme_asset_save/' + themeId + '/' + language + '?key=' + KEY,
                {
                    credentials: 'include',
                    body: JSON.stringify(usedItems),
                    method: 'POST'
                }, {language: language, theme: themeId})
                .then(data => data.json())
                .catch(e => e)

            // 20 strings per request, so aborting the one in flight does not end the loop.
            if(chunk && chunk.aborted === true) {
                response(chunk);
                return;
            }

            fetches.push(chunk)
        }
        for(let i = 0; i < prepareAssets.length; ++i) {
            const prepared = await _fetch(PROTOCOL + HOST + ENDPOINT + 'static_theme_asset_prepare/' + themeId + '/' + prepareAssets[i] + '/' + language + '?theme_write=' + themeWrite + '&key=' + KEY,
                {
                    credentials: 'include'
                }, {language: language, theme: themeId})
                .then(data => data.json())
                .catch(e => e)

            if(prepared && prepared.aborted === true) {
                response(prepared);
                return;
            }

            fetches.push(prepared)
        }

        Promise.all(fetches)
        .then(data => {
            response(data)
        })
        .catch(error => response(error))
    },
    syncNewTheme: (themeId, languageId, progress, response) => {
        progress(0)
        _fetch(PROTOCOL + HOST + ENDPOINT + 'new_theme/' + themeId + '/sync/' + languageId + '?key=' + KEY,
        {
            credentials: 'include'
        }, {language: languageId, theme: themeId})
        .then(data => data.json())
        .then(data => {
            if(data.aborted === true) {
                response(data);
                return;
            }
            progress(100)
            response(data);
        })
        .catch(error => response(error));
    },
    saveNewTheme: async (themeId, language, items, progress, response) => {
        // Nothing to write: with no chunks the loop below is a no-op anyway, and the full
        // re-read at the end would pointlessly replace the section for a save that saved nothing
        if(items.length === 0) {
            response({success: true});
            return;
        }

        const perPage = 15
        const numPages = Math.ceil(items.length / perPage)
        let fetches = []
        for(let i = 0; i < numPages; i++) {
            let usedItems = [];

            for(let j = 0; j < perPage; j++) {
                let index = (i * perPage) + j;
                if(index < items.length) {
									const data = {...items[index]};
									if(data.hasOwnProperty('text')) {
										usedItems.push(data);
									}
                }
            }

            const chunk = await _fetch(PROTOCOL + HOST + ENDPOINT + 'new_theme/' + themeId + '/save_all/' + language + '?key=' + KEY,
                {
                    credentials: 'include',
                    body: JSON.stringify(usedItems),
                    method: 'POST'
                }, {language: language, theme: themeId})
                .then(data => data.json())
                .catch(e => e)

            // Every chunk is a request of its own, so aborting the one in flight does not end
            // the loop - without this check the save keeps writing while the screen has
            // already moved on to another language.
            if(chunk && chunk.aborted === true) {
                response(chunk);
                return;
            }

            fetches.push(chunk)
        }

        Promise.all(fetches)
        // Despite the name, update_locale_file writes nothing: it re-reads the theme's full
        // string set and progress, so the answer replaces the chunk responses as one dataset
        .then(() =>
            _fetch(PROTOCOL + HOST + ENDPOINT + 'new_theme/' + themeId + '/update_locale_file/' + language + '?key=' + KEY,
            {
                credentials: 'include',
                method: 'POST'
            }, {language: language, theme: themeId})
        )
        .then(data => data.json())
        .then(data => {
            if(data.aborted === true) {
                response(data);
                return;
            }
            progress(100);
            response(data)
        })
        .catch(error => response(error))
    },
    syncTheme: (themeId, progress, response) => {
        progress(0)
        fetch(PROTOCOL + HOST + ENDPOINT + 'theme/' + themeId + '/sync?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            progress(100)
            response(data);
        })
        .catch(error => response(error));
    },
    syncCheckout: (themeId, languageId, progress, response) => {
        progress(0)
        fetch(PROTOCOL + HOST + ENDPOINT + 'theme/' + themeId + '/checkout/sync/' + languageId + '?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            progress(100)
            response(data);
        })
        .catch(error => response(error));
    },
    saveCheckout: (themeId, language, items, progress, response) => {
        progress(0)
        fetch(PROTOCOL + HOST + ENDPOINT + 'theme/' + themeId + '/checkout/save/' + language + '?key=' + KEY,
        {
            credentials: 'include',
            body: JSON.stringify(items),
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            progress(100);
            response(data)
        })
        .catch(error => response(error))
    },
    syncSections: (themeId, languageId, progress, response) => {
        progress(0)
        fetch(PROTOCOL + HOST + ENDPOINT + 'theme/' + themeId + '/sections/sync/' + languageId + '?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            progress(100)
            response(data);
        })
        .catch(error => response(error));
    },
    saveSections: (themeId, language, items, progress, response) => {
        progress(0)
        fetch(PROTOCOL + HOST + ENDPOINT + 'theme/' + themeId + '/sections/save/' + language + '?key=' + KEY,
        {
            credentials: 'include',
            body: JSON.stringify(items),
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            progress(100)
            response(data)
        })
        .catch(error => response(error))
    },
    syncAsset: (themeId, assetId, languageId, progress, response) => {
        progress(0)
        fetch(PROTOCOL + HOST + ENDPOINT + 'theme/' + themeId + '/asset/' + assetId + '/sync/' + languageId + '?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            progress(100)
            response(data);
        })
        .catch(error => response(error));
    },
    saveAsset: async (themeId, languageId, items, progress, response) => {
        let i = 0;
        let j = items.length
        let fetches = []
        for(let i = 0; i < j; i++) {
            fetches.push(
                await _fetch(PROTOCOL + HOST + ENDPOINT + 'theme/' + themeId + '/asset/save/' + languageId + '?key=' + KEY,
                {
                    credentials: 'include',
                    body: JSON.stringify(items[i]),
                    method: 'POST'
                })
                .then(data => data.json())
                .catch(e => e)
            )
        }

        Promise.all(fetches)
        .then(data => {
            response(data)
        })
        .catch(error => response(error))
    },
    syncImages: (themeId, languageId, progress, response) => {
        progress(0)
        fetch(PROTOCOL + HOST + ENDPOINT + 'images/' + themeId + '/sync/' + languageId + '?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            progress(100)
            response(data);
        })
        .catch(error => response(error));
    },
    syncNotifications: (languageId, progress, response) => {
        progress(0)
        _fetch(PROTOCOL + HOST + ENDPOINT + 'notifications/sync/' + languageId + '?key=' + KEY,
        {
            credentials: 'include'
        }, {language: languageId})
        .then(data => data.json())
        .then(data => {
            if(data.aborted === true) {
                response(data);
                return;
            }
            progress(100)
            response(data);
        })
        .catch(error => response(error));
    },
    saveNotifications: async (language, items, progress, response) => {
        let i = 0;
        let j = items.length
        let fetches = []
        for(let i = 0; i < j; i++) {
            const item = await _fetch(PROTOCOL + HOST + ENDPOINT + 'notifications/save/' + language + '?key=' + KEY,
                {
                    credentials: 'include',
                    body: JSON.stringify(items[i]),
                    method: 'POST'
                }, {language: language})
                .then(data => data.json())
                .catch(e => e)

            // One notification per request, so aborting the one in flight does not end the loop.
            if(item && item.aborted === true) {
                response(item);
                return;
            }

            fetches.push(item)
        }

        Promise.all(fetches)
        .then(data => {
            response(data)
        })
        .catch(error => response(error))
    },
    syncSMSNotifications: (language, progress, response) => {
        progress(0)
        fetch(PROTOCOL + HOST + ENDPOINT + 'sms_notifications/sync/' + language + '?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            progress(100)
            response(data);
        })
        .catch(error => response(error));
    },
    saveSMSNotifications: async (language, items, progress, response) => {
        let i = 0;
        let j = items.length
        let fetches = []
        for(let i = 0; i < j; i++) {
            fetches.push(
                await _fetch(PROTOCOL + HOST + ENDPOINT + 'sms_notifications/save/' + language + '?key=' + KEY,
                {
                    credentials: 'include',
                    body: JSON.stringify(items[i]),
                    method: 'POST'
                })
                .then(data => data.json())
                .catch(e => e)
            )
        }

        Promise.all(fetches)
        .then(data => {
            response(data)
        })
        .catch(error => response(error))
    },
    autoTranslateSingleString: (from, to, string, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'autotranslations/translate_single_string?key=' + KEY,
            {
                credentials: 'include',
                body: JSON.stringify({
                    strings: [string],
                    from: from,
                    to: to,
                    force: 'deepl' 
                }),
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            })
            .then(data => data.json())
            .then(data => {
              if(data.success === true){
                response(data.Strings[0]);
              } else {
                response(data.message);
              }
            })
            .catch(error => response(error));
    },
    autoTranslateStrings: (from, to, strings, response, process) => {
			// The words are deducted on the server as the chunks are processed, so the switch
			// warning is up for exactly as long as this request is out.
			API.beginProtectedRun();
			const runToken = API.runToken;
			let urls = [];
			let payloads = [];
			let chunkedStrings = chunkObject(strings, 1000);
			//console.log(chunkedStrings, strings);
			chunkedStrings.forEach(stringsChunk => {
				urls.push(PROTOCOL + HOST + ENDPOINT + 'autotranslations/translate?key=' + KEY);
				payloads.push({
					credentials: 'include',
					headers: {
						'x-referrer': window.location.href,
                        'Content-Type': 'application/json'
					},
					body: JSON.stringify({
						map: stringsChunk,
						from: from,
						to: to,
						disableCache: false,
					}),
					method: 'POST'
				})
			});
			// each finished chunk extends the backstop, so only a hung chunk can outlive it;
			// a cancelled run is never re-armed
			fetchSequential(urls, payloads, 0, [], () => {
				if(API.runToken === runToken) {
					API.beginProtectedRun();
				}
			})
				.then(data => {
					if(!data.error){
						let res = data[0];
						let allTranslatedStrings = {};
						data.forEach(x => {
							allTranslatedStrings = {...allTranslatedStrings, ...x.Strings};
						});
						res.Strings = allTranslatedStrings;
						API.endProtectedRun();
						response(res);
					} else {
						API.endProtectedRun();
						response(data.error);
					}
				})
				.catch(error => {
					API.endProtectedRun();
					response(error);
				});
    },
    autoTranslateWordCount: (from, to, map, response) => {
      fetch(PROTOCOL + HOST + ENDPOINT + 'autotranslations/wordcount?key=' + KEY, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-referrer': window.location.href },
        // skip_details: the service names the strings it would leave as they are, with a reason
        body: JSON.stringify({ map, from, to, disableCache: true, skip_details: true }),
        method: 'POST'
      })
      .then(data => data.json())
      .then(data => response(data))
      .catch(error => response(error));
    },
    buyWords: (plan, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'autotranslations/buy_words/' + plan + '?key=' + KEY,
            {
                credentials: 'include',
                method: 'GET'
            })
            .then(data => data.json())
            .then(data => {
                if(data.success === true) {
                    window.location.replace(data.confirmation_url)
                }
            })
            .catch(error => response(error));
    },
    updateCharge: (response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'application_charge/update?key=' + KEY,
            {
                credentials: 'include',
                method: 'GET'
            })
            .then(data => data.json())
            .then(data => {
                if(data.success === true) {
                    if(data.confirmation_url) {
                        window.location.replace(data.confirmation_url)
                    }
                }
            })
            .catch(error => response(error));
    },
    // POST, and the term in the body: a search term cannot survive a path segment - a raw
    // slash splits it and an encoded one is rejected before symfony routes. types may be
    // omitted for the default set
    search: (themeId, query, response, types) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'r_search/' + themeId + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify({ query: query, types: types }),
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    // one route for every paginable type. Term and cursor go in the body for the same
    // reason as r_search, and a shopify cursor is base64 so it cannot ride in a path
    searchMore: (type, query, cursor, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'search_more/' + type + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify({ query: query, cursor: cursor ? cursor : null }),
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    syncProductFromSearch: (id, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'search/sync_product/' + id + '?key=' + KEY,
        {
            credentials: 'include',
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    syncCollectionFromSearch: (id, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'search/sync_collection/' + id + '?key=' + KEY,
        {
            credentials: 'include',
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    cancelSearch: () =>{
    },
    getCustomAsset: (themeId, data, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'asset/' + themeId + '/get?key=' + KEY,
        {
            credentials: 'include',
            body: JSON.stringify(data),
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    restoreFromLocalAsset: async (themeId, data, response) => {
        if (!Array.isArray(data)) {
            fetch(PROTOCOL + HOST + ENDPOINT + 'local_asset/restore/' + themeId + '?key=' + KEY,
            {
                credentials: 'include',
                body: JSON.stringify(data),
                method: 'POST'
            })
            .then(data => data.json())
            .then(data => {
                response(data);
            })
            .catch(error => response(error));
        } else {
            let fetches = [];
            let _data = [];
            _data = data;

            for(let i = 0; i < _data.length; i++) {
                fetches.push(
                    await _fetch(PROTOCOL + HOST + ENDPOINT + 'local_asset/restore/' + themeId + '?key=' + KEY,
                    {
                        credentials: 'include',
                        body: JSON.stringify(_data[i]),
                        method: 'POST'
                    })
                    .then(data => data.json())
                    .then(data => {
                        console.log(data)
                        return data
                    })
                );
            }
            Promise.all(fetches)
            .then(data => {
                response(data);
            })
            .catch(error => response(error));  
        }
    },
    saveCustomAsset: (themeId, data, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'asset/' + themeId + '/save?key=' + KEY,
        {
            credentials: 'include',
            body: JSON.stringify(data),
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    saveCustomizedAssets: async (themeId, assets, response) => {
        const fetches = [];
        const j = assets.length;
        for(let i = 0; i < j; i++) {
            const asset = assets[i];

            fetches.push(
                await _fetch(PROTOCOL + HOST + ENDPOINT + 'asset/customized/' + themeId + '/save?key=' + KEY,
                {
                    credentials: 'include',
                    body: JSON.stringify(asset),
                    method: 'POST'
                })
                .then(data => data.json())
                .then(data => {
                    return data
                })
            )
        }

        Promise.all(fetches)
        .then(data => {
            response(data);
        })
        .catch();                
    },
    removeCustomAsset: (themeId, snippet, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'asset/' + themeId + '/remove?key=' + KEY,
        {
            credentials: 'include',
            body: JSON.stringify({key:snippet}),
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    saveCustomMetafield: (namespace, key, value, type, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'asset_metafield/save?key=' + KEY,
        {
            credentials: 'include',
            body: JSON.stringify({
                namespace: namespace,
                key: key,
                value: value,
                type: type
            }),
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    addV3Scripts: (themeId, response, themeWrite=true) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'add_v3_scripts/' + themeId + '?theme_write=' + themeWrite + '&key=' + KEY,
        {
            credentials: 'include',
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    removeV3Scripts: (themeId, response, themeWrite=true) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'remove_v3_scripts/' + themeId + '?theme_write=' + themeWrite + '&key=' + KEY,
        {
            credentials: 'include',
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    createURL: (action) => {
        return PROTOCOL + HOST + ENDPOINT + action + '?key=' + KEY
    },
    // several bulk operations can run at once since API 2026-01, each one is polled by its own id
    runBulkOperation: (createAction, response) => {
        const key = createAction + ':' + Date.now() + ':' + Math.random();
        const fail = (message, code) => {
            delete API.bulkOperationTimeouts[key];
            response({
                success: false,
                message: message,
                code: code
            });
        };
        let failedPolls = 0;
        const poll = id => {
            fetch(PROTOCOL + HOST + ENDPOINT + 'bulk_operation/status/' + id + '?key=' + KEY,
            {
                credentials: 'include'
            })
            .then(data => data.json())
            .then(data => {
                if(!(key in API.bulkOperationTimeouts)) {
                    return;
                }
                failedPolls = 0;
                const status = data.success === true ? data.operation.status : null;
                if(status == 'CREATED' || status == 'RUNNING') {
                    API.bulkOperationTimeouts[key] = setTimeout(() => poll(id), 1000);
                } else if(status == 'COMPLETED') {
                    delete API.bulkOperationTimeouts[key];
                    response(data);
                } else {
                    fail();
                }
            })
            .catch(error => {
                if(!(key in API.bulkOperationTimeouts)) {
                    return;
                }
                // one dropped status request shouldn't end an operation that is still running
                if(++failedPolls < 3) {
                    API.bulkOperationTimeouts[key] = setTimeout(() => poll(id), 2000);
                } else {
                    fail(error && error.message);
                }
            });
        };

        API.bulkOperationTimeouts[key] = null;
        fetch(PROTOCOL + HOST + ENDPOINT + createAction + '?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            if(!(key in API.bulkOperationTimeouts)) {
                return;
            }
            if(data.success == true) {
                // the number at the end of the gid, the backend builds the gid again
                API.bulkOperationTimeouts[key] = setTimeout(() => poll(data.operation.id.split('/').pop()), 1000);
            } else {
                // e.g. Shopify's OPERATION_IN_PROGRESS when 5 are running already
                fail(data.message, data.code);
            }
        })
        .catch(error => key in API.bulkOperationTimeouts && fail(error && error.message));
    },
    exportProducts: (language, options, progress, response) => {

        let queryAppendix = '';
        Object.keys(options).forEach(key => {
            if(options[key] === true) {
                queryAppendix = queryAppendix + `&${key}=${options[key]}`
            }
        });

        API.runBulkOperation('bulk_operation/products/create/' + language, data => {
            if(data.success !== true) {
                response({ ...data, message: data.message || 'Export failed' });
                return;
            }
            const exportURL = type => PROTOCOL + HOST + ENDPOINT + 'bulk_operation/products/export/' + type + '/' + language + '?key=' + KEY + '&operation_id=' + data.operation.id.split('/').pop() + queryAppendix;
            response({
                success: true,
                po_download_url: exportURL('po'),
                csv_download_url: exportURL('csv'),
                advanced_csv_download_url: exportURL('advanced_csv')
            })
        })
    },
    exportProductMetafields: (language, progress, response) => {
        API.runBulkOperation('bulk_operation/product_metafields/create/' + language, data => {
            if(data.success !== true) {
                response({ ...data, message: data.message || 'Export failed' });
                return;
            }
            const exportURL = type => PROTOCOL + HOST + ENDPOINT + 'bulk_operation/product_metafields/export/' + type + '/' + language + '?key=' + KEY + '&operation_id=' + data.operation.id.split('/').pop();
            response({
                success: true,
                po_download_url: exportURL('po'),
                csv_download_url: exportURL('csv'),
                advanced_csv_download_url: exportURL('advanced_csv')
            })
        })
    },
    exportProductsForImport: (language, progress, response) => {
        API.runBulkOperation('bulk_operation/products_import/create/' + language, data => {
            response(data.success === true ? { success: true, jsonl_url: data.operation.url } : data)
        })
    },
    exportProductMetafieldsForImport: (language, progress, response) => {
        API.runBulkOperation('bulk_operation/product_metafields_import/create/' + language, data => {
            response(data.success === true ? { success: true, jsonl_url: data.operation.url } : data)
        })
    },
    stopBulkOperationPolling: () => {
        Object.values(API.bulkOperationTimeouts).forEach(timeout => timeout && clearTimeout(timeout));
        API.bulkOperationTimeouts = {};
    },
    /*
    exportProducts: (language, progress, response) => {
        let ids = [];
        let tags = [];
        let types = [];
        let vendors = [];

        fetch(PROTOCOL + HOST + ENDPOINT + 'export_products/count/' + language + '?key=' + KEY,
            {
                credentials: 'include'
            })
            .then(data => data.json())
            .then(async data => {
                if(data.success === true) {
                    const count = data.count
                    const perPage = 30
                    const numPages = Math.ceil(count / perPage)

                    return API.__paginateItems('export_products/sync/start', language, 1, numPages, perPage, null, (data, currentProgress) => {
                        progress(currentProgress);
                        ids = ids.concat(data.ids)
                        tags = tags.concat(data.tags)
                        types = types.concat(data.types)
                        vendors = vendors.concat(data.vendors)
                    }, false)
                }
            })
            .then(data => {
                tags = tags.filter((item, pos) => {return tags.indexOf(item) == pos})
                types = types.filter((item, pos) => {return types.indexOf(item) == pos})
                vendors = vendors.filter((item, pos) => {return vendors.indexOf(item) == pos})

                return fetch(PROTOCOL + HOST + ENDPOINT + 'export_products/sync/end/' + language + '?key=' + KEY,
                    {
                        credentials: 'include',
                        method: 'POST',
                        body: JSON.stringify({ids:ids, tags:tags, types:types, vendors:vendors})
                    })
            })
            .then(data =>  data.json())
            .then(data => {
                progress(100)
                response(data)
            })
            .catch(error => response(error))
    },
    */
    importProducts: (file, fileType, options, language, progress, callback) => {
        const fail = message => callback({ success: false, message: message || 'Import failed' });

        API.__uploadImportFile('import_products/download/' + fileType + '/' + language, file, response => {
            let numImportedEntries = 0;
            // with an identifier the backend keeps the file's translations and the product map itself
            const importIdentifier = response.import_identifier ?? null
            const translations = importIdentifier ? null : response.translations

            API.exportProductsForImport(
                language,
                null,
                data => {
                    if(data.success !== true) {
                        fail(data.message);
                        return;
                    }
                    const url = data.jsonl_url;
                    fetch(PROTOCOL + HOST + ENDPOINT + 'import_products/count/lines?key=' + KEY,
                    {
                        credentials: 'include',
                        method: 'POST',
                        body: JSON.stringify({
                            url: url,
                            import_identifier: importIdentifier,
                            ...options
                        })
                    })
                    .then(data => data.json())
                    .then(async data => {
                        if(data.success !== true) {
                            fail(data.message);
                            return;
                        }
                        if (options.product_tags !== true &&
                            options.product_types !== true &&
                            options.product_vendors !== true &&
                            options.custom_contents !== true ) {
                            var perPage = 60;
                        } else {
                            var perPage = 20;
                        }
                        const map = importIdentifier ? null : data.map;

                        // Calc a new numPages value depending on the enabled importOptions and the line counts returned by the /count/lines API endpoint.
                        let lineCount = data.products_lines;
                        if(options.product_tags === true) lineCount += data.tags_count;
                        if(options.product_types === true) lineCount += data.types_count;
                        if(options.product_vendors === true) lineCount += data.vendors_count;
                        if(options.custom_contents === true) lineCount += data.custom_contents_count;
                        const numPages = Math.ceil(lineCount / perPage);

                        const result = await API.__importTranslations(
                            'import_product_translations',
                            language,
                            url,
                            1,
                            numPages,
                            perPage,
                            translations,
                            map,
                            1,
                            importIdentifier,
                            data => {
                                numImportedEntries += data.num_imported_entries;
                            },
                            (currentProgress) => {
                                progress(currentProgress);
                            },
                            options)
                        if(result.success !== true) {
                            fail(result.message);
                            return;
                        }
                        callback({
                            success: true,
                            num_imported_entries: numImportedEntries
                        });
                    })
                    .catch(error => fail(error && error.message))
                })
        }, fail, () => callback({ cancelled: true }));
    },
    importProductMetafields: (file, fileType, language, progress, callback) => {
        const fail = message => callback({ success: false, message: message || 'Import failed' });

        API.__uploadImportFile('import_product_metafields/download/' + fileType + '/' + language, file, response => {
            let numImportedEntries = 0;
            const translations = response.translations
            const importIdentifier = response.import_identifier

            API.exportProductMetafieldsForImport(
                language,
                null,
                data => {
                    if(data.success !== true) {
                        fail(data.message);
                        return;
                    }
                    const url = data.jsonl_url;
                    fetch(PROTOCOL + HOST + ENDPOINT + 'import_product_metafields/count/lines?key=' + KEY,
                    {
                        credentials: 'include',
                        method: 'POST',
                        body: JSON.stringify({url: url})
                    })
                    .then(data => data.json())
                    .then(async data => {
                        if(data.success !== true) {
                            fail(data.message);
                            return;
                        }
                        const count = data.lines;
                        const perPage = 60;
                        const numPages = Math.ceil(count / perPage);
                        const map = data.map;

                        const result = await API.__importTranslations(
                            'import_product_metafield_translations',
                            language,
                            url,
                            1,
                            numPages,
                            perPage,
                            translations,
                            map,
                            1,
                            importIdentifier,
                            data => {
                                numImportedEntries += data.num_imported_entries;
                            },
                            (currentProgress) => {
                                progress(currentProgress);
                            })
                        if(result.success !== true) {
                            fail(result.message);
                            return;
                        }
                        callback({
                            success: true,
                            num_imported_entries: numImportedEntries
                        });
                    })
                    .catch(error => fail(error && error.message))
                })
        }, fail, () => callback({ cancelled: true }));
    },
    exportCollections: (language, options, progress, response) => {
        let ids = [];
        let perPage = 50
        let numPages = 0

        let queryAppendix = '?';
        Object.keys(options).forEach(key => {
            if(options[key] === true) {
                queryAppendix = queryAppendix + `&${key}=${options[key]}`
            }
        });
        queryAppendix = queryAppendix.replace('?&', '?');

        fetch(PROTOCOL + HOST + ENDPOINT + 'export_collections/count/' + language + '?key=' + KEY,
            {
                credentials: 'include'
            })
            .then(data => data.json())
            .then(async data => {
                if(data.success === true) {
                    numPages = Math.ceil(data.count / perPage)

                    return API.__paginateItems('export_collections/custom/sync/start', language, 1, numPages, perPage, null, (data, currentProgress) => {
                        progress(currentProgress);
                        ids = ids.concat(data.ids)
                    }, false, options)
                }
            })
            .then(data => {
                return fetch(PROTOCOL + HOST + ENDPOINT + 'export_collections/sync/end/' + language + '?key=' + KEY,
                    {
                        credentials: 'include',
                        method: 'POST',
                        body: JSON.stringify(ids)
                    })
            })
            .then(data =>  data.json())
            .then(data => {
                progress(100)
                if(data.success) {
                    data.csv_download_url = data.csv_download_url + queryAppendix;
                    data.po_download_url = data.po_download_url + queryAppendix;
                }
                response(data)
            })
            .catch(error => response(error))
    },
    importCollections: (file, fileType, language, options, progress, callback) => {
        API.__importListInPages('import_collections/download/' + fileType + '/' + language, file, response =>
            chunkArray(response.collections, 20).map(items => ({
                url: PROTOCOL + HOST + ENDPOINT + 'import_collection_translations/' + language + '?key=' + KEY,
                body: { translations: response.translations, collections: items, ...options }
            })), progress, callback);
    },
    exportPages: (language, options, progress, response) => {
        let ids = [];
        let queryAppendix = '?';
        Object.keys(options).forEach(key => {
            if(options[key] === true) {
                queryAppendix = queryAppendix + `&${key}=${options[key]}`
            }
        });
        queryAppendix = queryAppendix.replace('?&', '?');

        fetch(PROTOCOL + HOST + ENDPOINT + 'export_pages/count/' + language + '?key=' + KEY,
            {
                credentials: 'include'
            })
            .then(data => data.json())
            .then(async data => {
                if(data.success === true) {
                    const count = data.count
                    const perPage = 50
                    const numPages = Math.ceil(count / perPage)

                    return API.__paginateItems('export_pages/sync/start', language, 1, numPages, perPage, null, (data, currentProgress) => {
                        progress(currentProgress);
                        ids = ids.concat(data.ids)
                    }, false, options)
                }
            })
            .then(data => {
                return fetch(PROTOCOL + HOST + ENDPOINT + 'export_pages/sync/end/' + language + '?key=' + KEY,
                    {
                        credentials: 'include',
                        method: 'POST',
                        body: JSON.stringify(ids)
                    })
            })
            .then(data =>  data.json())
            .then(data => {
                progress(100)
                if(data.success) {
                    data.csv_download_url = data.csv_download_url + queryAppendix;
                    data.po_download_url = data.po_download_url + queryAppendix;
                }
                response(data)
            })
            .catch(error => response(error))
    },
    importPages: (file, fileType, language, options, progress, callback) => {
        API.__importListInPages('import_pages/download/' + fileType + '/' + language, file, response =>
            chunkArray(response.pages, 20).map(items => ({
                url: PROTOCOL + HOST + ENDPOINT + 'import_page_translations/' + language + '?key=' + KEY,
                body: { translations: response.translations, pages: items, ...options }
            })), progress, callback);
    },
    exportNavigation: (language, options, progress, response) => {

        let queryAppendix = '?';
        Object.keys(options).forEach(key => {
            if(options[key] === true) {
                queryAppendix = queryAppendix + `&${key}=${options[key]}`
            }
        });
        queryAppendix = queryAppendix.replace('?&', '?');

        fetch(PROTOCOL + HOST + ENDPOINT + 'export_navigation/sync/' + language + '?key=' + KEY,
            {
                credentials: 'include'
            })
            .then(data =>  data.json())
            .then(data => {
                progress(100)
                if(data.success) {
                    data.csv_download_url = data.csv_download_url + queryAppendix;
                    data.po_download_url = data.po_download_url + queryAppendix;
                }
                response(data)
            })
            .catch(error => response(error))
    },
    importNavigation: (file, fileType, language, progress, callback) => {
        API.__importListInPages('import_navigation/download/' + fileType + '/' + language, file, response =>
            chunkArray(response.links, 20).map(items => ({
                url: PROTOCOL + HOST + ENDPOINT + 'import_navigation_translations/' + language + '?key=' + KEY,
                body: { translations: response.translations, links: items }
            })), progress, callback);
    },
    exportCustomContents: (language, progress, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'export_custom_contents/sync/' + language + '?key=' + KEY,
            {
                credentials: 'include'
            })
            .then(data =>  data.json())
            .then(data => {
                progress(100)
                response(data)
            })
            .catch(error => response(error))
    },
    importCustomContents: (file, fileType, language, progress, callback) => {
        API.__importListInPages('import_custom_contents/download/' + fileType + '/' + language, file, response =>
            chunkArray(response.custom_contents, 20).map(items => ({
                url: PROTOCOL + HOST + ENDPOINT + 'import_custom_content_translations/' + language + '?key=' + KEY,
                body: { translations: response.translations, custom_contents: items }
            })), progress, callback);
    },
    exportNewCustomContents: (language, options, progress, response) => {

        let queryAppendix = '?';
        Object.keys(options).forEach(key => {
            if(options[key] === true) {
                queryAppendix = queryAppendix + `&${key}=${options[key]}`
            }
        });
        queryAppendix = queryAppendix.replace('?&', '?');

        fetch(PROTOCOL + HOST + ENDPOINT + 'export_new_custom_contents/sync/' + language + '?key=' + KEY,
            {
                credentials: 'include'
            })
            .then(data =>  data.json())
            .then(data => {
                progress(100)
                if(data.success) {
                    data.csv_download_url = data.csv_download_url + queryAppendix;
                    data.po_download_url = data.po_download_url + queryAppendix;
                }
                response(data)
            })
            .catch(error => response(error))
    },
    importNewCustomContents: (file, fileType, language, progress, callback) => {
        API.__importListInPages('import_new_custom_contents/download/' + fileType + '/' + language, file, response =>
            chunkArray(response.new_custom_contents, 20).map(items => ({
                url: PROTOCOL + HOST + ENDPOINT + 'import_new_custom_content_translations/' + language + '?key=' + KEY,
                body: { translations: response.translations, new_custom_contents: items }
            })), progress, callback);
    },
    exportBlogs: (language, options, progress, response) => {
        let queryAppendix = '?';
        Object.keys(options).forEach(key => {
            if(options[key] === true) {
                queryAppendix = queryAppendix + `&${key}=${options[key]}`
            }
        });
        fetch(PROTOCOL + HOST + ENDPOINT + 'export_blogs/sync/' + language + '?key=' + KEY,
            {
                credentials: 'include'
            })
            .then(data =>  data.json())
            .then(data => {
                progress(100)
                if(data.success) {
                    data.csv_download_url = data.csv_download_url + queryAppendix;
                    data.po_download_url = data.po_download_url + queryAppendix;
                }
                response(data)
            })
            .catch(error => response(error))
    },
    importBlogs: (file, fileType, language, options, progress, callback) => {
        API.__importListInPages('import_blogs/download/' + fileType + '/' + language, file, response =>
            chunkArray(response.blogs, 20).map(items => ({
                url: PROTOCOL + HOST + ENDPOINT + 'import_blog_translations/' + language + '?key=' + KEY,
                body: { translations: response.translations, blogs: items, ...options }
            })), progress, callback);
    },
    exportArticles: (language, options, progress, response) => {
        let ids = [];
        let articles = [];
        let queryAppendix = '?';
        Object.keys(options).forEach(key => {
            if(options[key] === true) {
                queryAppendix = queryAppendix + `&${key}=${options[key]}`
            }
        });

        fetch(PROTOCOL + HOST + ENDPOINT + 'blogs/sync/' + language + '?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            let fetches = []
            
            data.blogs.forEach(item => {
                fetches.push(
                    _fetch(PROTOCOL + HOST + ENDPOINT + 'export_articles/' + item.id + '/count/' + language + '?key=' + KEY,
                    {
                        credentials: 'include'
                    })
                    .then(data => data.json())
                    .then(async data => {
                        if(data.success === true) {
                            const count = data.count
                            const perPage = 50
                            const numPages = Math.ceil(count / perPage)

                            return API.__paginateItems('export_articles/' + item.id + '/sync/start', language, 1, numPages, perPage, null, (data, currentProgress) => {
                                progress(currentProgress);
                                ids = ids.concat(data.ids)
                            }, false, options)
                        }
                    })
                    .then(data => {
                        return fetch(PROTOCOL + HOST + ENDPOINT + 'export_articles/' + item.id + '/sync/end/' + language + '?key=' + KEY,
                            {
                                credentials: 'include',
                                method: 'POST',
                                body: JSON.stringify(ids)
                            })
                    })
                    .then(data => data.json())
                    .then(data => {
                        progress(100)
                        if(data.success) {
                            data.csv_download_url = data.csv_download_url + queryAppendix;
                            data.po_download_url = data.po_download_url + queryAppendix;
                        }
                        response(data)
                    })
                )
            })

            return Promise.all(fetches)
        })
        .catch(error => response(error));
    },
    importArticles: (file, fileType, language, options, progress, callback) => {
        API.__importListInPages('import_articles/download/' + fileType + '/' + language, file, response =>
            chunkArray(response.articles, 20).map(items => ({
                url: PROTOCOL + HOST + ENDPOINT + 'import_article_translations/' + language + '?key=' + KEY,
                body: { translations: response.translations, articles: items, ...options }
            })), progress, callback);
    },
    exportNotifications: (language, options, progress, response) => {

        let queryAppendix = '?';
        Object.keys(options).forEach(key => {
            if(options[key] === true) {
                queryAppendix = queryAppendix + `&${key}=${options[key]}`
            }
        });
        queryAppendix = queryAppendix.replace('?&', '?');

        fetch(PROTOCOL + HOST + ENDPOINT + 'export_notifications/sync/' + language + '?key=' + KEY,
            {
                credentials: 'include'
            })
            .then(data =>  data.json())
            .then(data => {
                progress(100)
                if(data.success) {
                    data.csv_download_url = data.csv_download_url + queryAppendix;
                    data.po_download_url = data.po_download_url + queryAppendix;
                }
                response(data)
            })
            .catch(error => response(error))
    },
    importNotifications: (file, fileType, language, progress, callback) => {
        API.__importListInPages('import_notifications/download/' + fileType + '/' + language, file, response =>
            chunkArray(response.notifications, 20).map(items => ({
                url: PROTOCOL + HOST + ENDPOINT + 'import_notification_translations/' + language + '?key=' + KEY,
                body: { translations: response.translations, notifications: items }
            })), progress, callback);
    },
    exportSMSNotifications: (language, progress, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'export_sms_notifications/sync/' + language + '?key=' + KEY,
            {
                credentials: 'include'
            })
            .then(data =>  data.json())
            .then(data => {
                progress(100)
                response(data)
            })
            .catch(error => response(error))
    },
    importSMSNotifications: (file, fileType, language, progress, callback) => {
        API.__importListInPages('import_sms_notifications/download/' + fileType + '/' + language, file, response =>
            chunkArray(response.notifications, 20).map(items => ({
                url: PROTOCOL + HOST + ENDPOINT + 'import_sms_notification_translations/' + language + '?key=' + KEY,
                body: { translations: response.translations, notifications: items }
            })), progress, callback);
    },
    exportNewTheme: (theme, options, language, progress, response) => {

        let queryAppendix = '?';
        Object.keys(options).forEach(key => {
            if(options[key] === true) {
                queryAppendix = queryAppendix + `&${key}=${options[key]}`
            }
        });
        queryAppendix = queryAppendix.replace('?&', '?');

        fetch(PROTOCOL + HOST + ENDPOINT + 'export_new_theme/sync/' + theme + '/' + language + '?key=' + KEY,
            {
                credentials: 'include'
            })
            .then(data =>  data.json())
            .then(data => {
                progress(100)
                if(data.success) {
                    data.csv_download_url = data.csv_download_url + queryAppendix;
                    data.advanced_csv_download_url = data.advanced_csv_download_url + queryAppendix;
                    data.po_download_url = data.po_download_url + queryAppendix;
                }
                response(data)
            })
            .catch(error => response(error))
    },
    importNewTheme: (file, fileType, theme, language, progress, callback) => {
        API.__importListInPages('import_new_theme/download/' + fileType + '/' + theme + '/' + language, file, response =>
            chunkArray(response.theme_strings, 10).map(strings => ({
                url: PROTOCOL + HOST + ENDPOINT + 'import_new_theme_translations/' + theme + '/' + language + '?key=' + KEY,
                body: { translations: response.translations, strings: strings }
            })), progress, callback);
    },
    enableLogging: (response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'dev/logger/enable/1?key=' + KEY,
        {
            credentials: 'include',
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch();
    },
    disableLogging: (response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'dev/logger/enable/0?key=' + KEY,
        {
            credentials: 'include',
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch();
    },
    getTranslationProgress: (language, theme, progress, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'stats/get_progress/' + language + '/'+ + theme + '?key=' + KEY,
        {
            credentials: 'include',
        })
        .then(data => data.json())
        .then(data => {
            progress(100);
            response(data);
        })
        .catch();
    },
    getRecentlyChangedContent: (filter, language, progress, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'stats/get_recently_changed_content/' + filter + '/' + language + '?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            progress(100);
            response(data);
        })
        .catch();

    },
    getMoreRecentlyChangedContent: (pageLinks, language, progress, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'stats/get_more_recently_changed_content/' + language + '?key=' + KEY,
        {
            credentials: 'include',
            body: JSON.stringify({'page_links': pageLinks}),
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            progress(100);
            response(data);
        })
        .catch();

    },
    syncProductByShopifyId: (id, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'sync_by_shopify_id/sync_product/' + id + '?key=' + KEY,
        {
            credentials: 'include',
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch();
    },
    syncCollectionByShopifyId: (id, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'sync_by_shopify_id/sync_collection/' + id + '?key=' + KEY,
        {
            credentials: 'include',
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch();
    },
    syncBlogByShopifyId: (id, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'sync_by_shopify_id/sync_blog/' + id + '?key=' + KEY,
        {
            credentials: 'include',
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch();
    },
    syncPageByShopifyId: (id, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'sync_by_shopify_id/sync_page/' + id + '?key=' + KEY,
        {
            credentials: 'include',
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch();
    },
    syncArticleByShopifyId: (id, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'sync_by_shopify_id/sync_article/' + id + '?key=' + KEY,
        {
            credentials: 'include',
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch();
    },
    syncProductImages: (productId, languageId, progress, response) => {
        progress(0)
        _fetch(PROTOCOL + HOST + ENDPOINT + 'product/images/sync/' + productId + '/' + languageId + '?key=' + KEY,
        {
            credentials: 'include'
        }, {language: languageId})
        .then(data => data.json())
        .then(data => {
            progress(100)
            response(data);
        })
        .catch(error => response(error));
    },
    uploadMedia: (formData, callback) => {
        var xhr = new XMLHttpRequest();
        xhr.withCredentials = true;
        // every outcome must reach the callback - callers show pending state until it fires
        xhr.onload = async function () {
          if(!callback) return;
          if(xhr.status === 200) {
            let response;
            try {
              response = JSON.parse(xhr.response);
            } catch {
              response = { success: false, message: 'Invalid server response' };
            }
            callback(response);
          } else {
            callback({ success: false, message: 'Upload failed (HTTP ' + xhr.status + ')' });
          }
        };
        xhr.onerror = function () {
          if(callback) callback({ success: false, message: 'Network error during upload' });
        };

        xhr.open('POST', PROTOCOL + HOST + ENDPOINT + 'media/upload?key=' + KEY, true);
        xhr.send(formData);
    },
    getNewCustomContents: (language, response) => {
        _fetch(PROTOCOL + HOST + ENDPOINT + 'new_custom_contents/get/' + language + '?key=' + KEY,
        {
            credentials: 'include'
        }, {language: language})
        .then(data => data.json())
        .then(data => {
            response(data);
      })
      .catch(error => response(error));
    },
    createNewCustomContents: async (language, items, progress, response) => {
        let i = 0;
        let j = items.length
        let fetches = []
        for(let i = 0; i < j; i++) {
            const chunk = await _fetch(PROTOCOL + HOST + ENDPOINT + 'new_custom_contents/create/' + language + '?key=' + KEY,
                {
                    credentials: 'include',
                    body: JSON.stringify(items[i]),
                    method: 'POST'
                }, {language: language})
                .then(data => data.json())
                .catch(e => e)

            if(chunk && chunk.aborted === true) {
                response(chunk);
                return;
            }

            fetches.push(chunk)
        }

        Promise.all(fetches)
        .then(data => {
            response(data)
        })
        .catch(error => response(error))
    },
    deleteNewCustomContent: async (id, deprecated, progress, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'new_custom_contents/delete/' + id + '/' + deprecated +  '?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    // entries: [{id, deprecated}]; batches of 10 so the overlay shows progress and a failure
    // loses little. Answers {success, deleted: [{id, scope}], failed: [ids]} over all batches
    deleteNewCustomContents: async (entries, progress, response) => {
        const perBatch = 10;
        const deleted = [];
        const failed = [];
        for(let i = 0; i < entries.length; i += perBatch) {
            const batch = entries.slice(i, i + perBatch);
            try {
                const data = await _fetch(PROTOCOL + HOST + ENDPOINT + 'new_custom_contents/delete_bulk?key=' + KEY,
                {
                    credentials: 'include',
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ids: batch })
                }).then(data => data.json());
                if(!data || data.success !== true) {
                    response(data || { success: false });
                    return;
                }
                deleted.push(...(data.deleted || []));
                failed.push(...(data.failed || []));
            } catch(error) {
                response(error);
                return;
            }
            progress(Math.round(Math.min(i + perBatch, entries.length) / entries.length * 100));
        }
        response({ success: true, deleted: deleted, failed: failed });
    },
    saveNewCustomContents: async (language, items, progress, response) => {
        const perPage = 40;
        const numPages = Math.ceil(items.length / perPage);

        let fetches = [];
        for(let i = 0; i < numPages; i++) {
            let usedItems = [];

            for(let j = 0; j < perPage; j++) {
                let index = (i * perPage) + j;
                if(index < items.length) {
                    usedItems.push({...items[index]})
                }
            }

            const chunk = await _fetch(PROTOCOL + HOST + ENDPOINT + 'new_custom_contents/save/' + language + '?key=' + KEY,
                {
                    credentials: 'include',
                    body: JSON.stringify(usedItems),
                    method: 'POST'
                }, {language: language})
                .then(data => data.json())
                .catch(e => e)

            if(chunk && chunk.aborted === true) {
                response(chunk);
                return;
            }

            fetches.push(chunk)
        }

        Promise.all(fetches)
        .then(data => {
            response(data)
        })
        .catch(error => response(error))
    },
    createProductCustomContents: async (product, language, items, progress, response) => {
        let i = 0;
        let j = items.length
        let fetches = []
        for(let i = 0; i < j; i++) {
            fetches.push(
                await _fetch(PROTOCOL + HOST + ENDPOINT + 'product/custom_contents/create/' + product + '/' + language + '?key=' + KEY,
                {
                    credentials: 'include',
                    body: JSON.stringify(items[i]),
                    method: 'POST'
                })
                .then(data => data.json())
                .catch(e => e)
            )
        }

        Promise.all(fetches)
        .then(data => {
            response(data)
        })
        .catch(error => response(error))
    },
    deleteProductCustomContent: async (product, id, progress, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'product/custom_contents/delete/'  + product + '/' + id + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    createCollectionCustomContents: async (collection, language, items, progress, response) => {
        let i = 0;
        let j = items.length
        let fetches = []
        for(let i = 0; i < j; i++) {
            fetches.push(
                await _fetch(PROTOCOL + HOST + ENDPOINT + 'collection/custom_contents/create/' + collection + '/' + language + '?key=' + KEY,
                {
                    credentials: 'include',
                    body: JSON.stringify(items[i]),
                    method: 'POST'
                })
                .then(data => data.json())
                .catch(e => e)
            )
        }

        Promise.all(fetches)
        .then(data => {
            response(data)
        })
        .catch(error => response(error))
    },
    deleteCollectionCustomContent: async (collection, id, progress, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'collection/custom_contents/delete/'  + collection + '/' + id + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    createPageCustomContents: async (page, language, items, progress, response) => {
        let i = 0;
        let j = items.length
        let fetches = []
        for(let i = 0; i < j; i++) {
            fetches.push(
                await _fetch(PROTOCOL + HOST + ENDPOINT + 'page/custom_contents/create/' + page + '/' + language + '?key=' + KEY,
                {
                    credentials: 'include',
                    body: JSON.stringify(items[i]),
                    method: 'POST'
                })
                .then(data => data.json())
                .catch(e => e)
            )
        }

        Promise.all(fetches)
        .then(data => {
            response(data)
        })
        .catch(error => response(error))
    },
    deletePageCustomContent: async (page, id, progress, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'page/custom_contents/delete/'  + page + '/' + id + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    createBlogCustomContents: async (blog, language, items, progress, response) => {
        let i = 0;
        let j = items.length
        let fetches = []
        for(let i = 0; i < j; i++) {
            fetches.push(
                await _fetch(PROTOCOL + HOST + ENDPOINT + 'blog/custom_contents/create/' + blog + '/' + language + '?key=' + KEY,
                {
                    credentials: 'include',
                    body: JSON.stringify(items[i]),
                    method: 'POST'
                })
                .then(data => data.json())
                .catch(e => e)
            )
        }

        Promise.all(fetches)
        .then(data => {
            response(data)
        })
        .catch(error => response(error))
    },
    deleteBlogCustomContent: async (blog, id, progress, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'blog/custom_contents/delete/'  + blog + '/' + id + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    createArticleCustomContents: async (article, language, items, progress, response) => {
        let i = 0;
        let j = items.length
        let fetches = []
        for(let i = 0; i < j; i++) {
            fetches.push(
                await _fetch(PROTOCOL + HOST + ENDPOINT + 'article/custom_contents/create/' + article + '/' + language + '?key=' + KEY,
                {
                    credentials: 'include',
                    body: JSON.stringify(items[i]),
                    method: 'POST'
                })
                .then(data => data.json())
                .catch(e => e)
            )
        }

        Promise.all(fetches)
        .then(data => {
            response(data)
        })
        .catch(error => response(error))
    },
    deleteArticleCustomContent: async (article, id, progress, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'article/custom_contents/delete/'  + article + '/' + id + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    exportStaticTheme: (themeId, options, languageId, progress, response) => {

        let queryAppendix = '?';
        Object.keys(options).forEach(key => {
            if(options[key] === true) {
                queryAppendix = queryAppendix + `&${key}=${options[key]}`
            }
        });
        queryAppendix = queryAppendix.replace('?&', '?');

        progress(0);

        fetch(PROTOCOL + HOST + ENDPOINT + 'static_theme/' + themeId + '/sync/' + languageId + '?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(async data => {
            let fetches = [];
            const count = data.assets.length;
            for(let i = 0; i < count; ++i) {
                fetches.push(
                    await _fetch(PROTOCOL + HOST + ENDPOINT + 'static_theme_asset/' + themeId + '/' + data.assets[i].id + '/sync/' + languageId + '?key=' + KEY,
                    {
                        credentials: 'include'
                    })
                    .then(data => {
                        progress(Math.floor(i / (count + 1) * 100));
                    })
                )
            }

            fetches.push(
                _fetch(PROTOCOL + HOST + ENDPOINT + 'export_static_theme/sync/' + themeId + '/' + languageId + '?key=' + KEY,
                {
                    credentials: 'include'
                })
                .then(data =>  data.json())
                .then(data => {
                    if(data.success) {
                        data.csv_download_url = data.csv_download_url + queryAppendix;
                        data.po_download_url = data.po_download_url + queryAppendix;
                    }
                    response(data)
                })
            )

            return Promise.all(fetches);
        })
        .catch(error => response(error));
    },
    importStaticTheme: (file, fileType, theme, language, progress, callback) => {
        API.__importListInPages('import_static_theme/download/' + fileType + '/' + theme + '/' + language, file, response =>
            response.assets.map(asset => ({
                url: PROTOCOL + HOST + ENDPOINT + 'import_static_theme_translations/' + theme + '/' + asset + '/' + language + '?key=' + KEY,
                body: { translations: response.translations }
            })), progress, callback);
    },
    enableCrossDomainLinks: (response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'shop/enable_cross_domain_links?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch();
    },
    disableCrossDomainLinks: (response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'shop/disable_cross_domain_links?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch();
    },
    importV1ProductMetafieldTranslations: (language, product, metafields, progress, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'admin/import_v1_product_metafield_translations/' + product + '/' + language + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify(metafields)
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch();
    },

    bulkCountStart: async (store, from, to, scope, subObject, gids, outdated, response) => {
			let body = {};
			if(gids.length > 0) {
				body = {
					gids: gids
				};
			}
      await _fetch(`${PROTOCOL + HOST + ENDPOINT}bulk_translation/count/${from}/${to}/${scope}${subObject && '/'+subObject}?key=${KEY}${outdated ? '&outdated=true' : ''}`,
      {
          credentials: 'include',
          method: 'POST',
					body: JSON.stringify(body)
      }, 1000)
      .then(data => data.json())
      .then(data => {
        response(data);
      })
      .catch(error => response(error));
    },
    bulkTranslateDirectStart: async (store, from, to, scope, subObject, gids, outdated, response) => {
      let body = {};
      if(gids.length > 0) {
        body = {
          gids: gids
        };
      }
      await _fetch(`${PROTOCOL + HOST + ENDPOINT}bulk_translation/translate_direct/${from}/${to}/${scope}${subObject && '/'+subObject}?key=${KEY}${outdated ? '&outdated=true' : ''}`,
      {
          credentials: 'include',
          method: 'POST',
          body: JSON.stringify(body)
      }, 1000)
      .then(data => data.json())
      .then(data => {
        response(data);
      })
      .catch(error => response(error));
    },
    bulkTranslationStart: async (store, key, response) => {
      await _fetch(`${PROTOCOL + HOST + ENDPOINT}bulk_translation/translate/${key}?key=${KEY}`,
          {
              credentials: 'include',
              method: 'POST'
          }, 1000)
      .then(data => data.json())
      .then(data => {
        response(data);

        /*
        if(data.success && data.key) {
          let results = null;
          API._queueProgressTasks(store, data.key, scope, res => {
            results = res;
            progress(results);
            
            if(results.task && results.task.status === 'completed') {
                response(results);
            }
          });
        } else {
          throw 'Could not get task key';
        }
        */
      })
      .catch(error => response(error));
    },
    bulkTranslationGetRunningTasks: (response) => {
        fetch(`${PROTOCOL + HOST + ENDPOINT}bulk_translation/running_tasks?key=${KEY}`,
        {
            credentials: 'include',
            method: 'GET'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    bulkTranslationClearTask: async (cacheKey) => {
        const response = await fetch(`${PROTOCOL + HOST + ENDPOINT}bulk_translation/running_tasks/${cacheKey}?key=${KEY}`,
        {
            credentials: 'include',
            method: 'DELETE'
        })
        const data = await response.json();
        return data;
    },
    bulkTranslationCancelTask: async (cacheKey) => {
        const response = await fetch(`${PROTOCOL + HOST + ENDPOINT}bulk_translation/running_tasks/${cacheKey}/cancel?key=${KEY}`,
        {
            credentials: 'include',
            method: 'POST'
        })
        const data = await response.json();
        return data;
    },
    bulkTranslationGetWordpacks: (response) => {
        fetch(`${PROTOCOL + HOST + ENDPOINT}bulk_translation/wordpacks?key=${KEY}`,
        {
            credentials: 'include',
            method: 'GET'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    bulkTranslationGetWordpack: (hash, response) => {
        fetch(`${PROTOCOL + HOST + ENDPOINT}bulk_translation/wordpack/get/${hash}?key=${KEY}`,
        {
            credentials: 'include',
            method: 'GET'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    bulkTranslationBuyWordpack: (hash, response) => {
        fetch(`${PROTOCOL + HOST + ENDPOINT}bulk_translation/wordpack/buy/${hash}?key=${KEY}`,
        {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify({
                returnUrl: PROTOCOL + RETURN_URL
            })
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    _queueProgressTasks: async (store, key, scope, callback) => {
        let endTaskStatus = ['completed', 'failed', 'expired'];

      return await fetch(`${PROTOCOL + HOST + ENDPOINT}bulk_translation/progress/${key}?key=${KEY}`,
          {
              credentials: 'include'
          })
        .then(data => data.json())
        .then(data => {
                //console.log(`ly_bulk_translate:${store}:${data.task.parameter.to}_${scope}`, store, data)

            //
            if(data.task && (data.task.type === 'translate' || data.task.type === 'count')) {
                window.localStorage.setItem(`ly_bulk_translate:${store}:${data.task.parameter.to}_${scope}`, JSON.stringify(data));
            }

            if(data.task && endTaskStatus.indexOf(data.task.status) === -1) {

                callback(data);
                setTimeout(() => API._queueProgressTasks(store, key, scope, callback), 1000);
            } else {

                callback(data);
            }
        })
        .catch(error => {
            callback(error)
        });
    },
    splitPOfile: (file, language, progress, response) => {
        var filename = "PO_split_parts.zip";
        fetch(PROTOCOL + HOST + ENDPOINT + 'admin/split_po/' + language + '?key=' + KEY, {
            credentials: 'include',
            method: 'POST',
            body: file,
        })
        .then(data =>  data.blob())
        .then((data) => {
            // (C1) FILE DATA IS "READY FOR USE"
            console.log(data);
        
            // (C2) TO "FORCE DOWNLOAD"
            var url = window.URL.createObjectURL(data),
            anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = filename;
            anchor.click();
        
            // (C3) CLEAN UP
            window.URL.revokeObjectURL(url);
            document.removeChild(anchor);
        })
        /*
        .then((result) => {
            console.log(result)
            if (!result.ok) {
                throw Error(result.statusText);
            }
            return result.blob(); 
        })
        .then(data => {
            //const file = window.URL.createObjectURL(data);
            //window.location.assign(file);

            
            if (data != null) {
                var url = window.URL.createObjectURL(data);
                var a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
            }

            response(data);
        })
        */
        .catch(error => response(error));
    },
    getMetafield: (mf_namespace, mf_key, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'asset_metafield/get/' + mf_namespace + '/' + mf_key + '?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch();
    },
    getScriptSettings: (shopifyThemeId, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'asset_script_settings/get/' + shopifyThemeId + '?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch();
    },
    // a window.langify.settings script in the theme, which overrides the theme settings metafield
    getCustomSettingsScript: (shopifyThemeId, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'asset_custom_settings_script/get/' + shopifyThemeId + '?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch();
    },
    getLangifyNotifications: (response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'langify/notifications/get?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch();
    },
    setLangifyNotificationAsViewed: (notificationId, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'langify/notifications/set_as_viewed/' + notificationId + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch();
    },
    setLangifyNotificationAsUnviewed: (notificationId, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'langify/notifications/set_as_unviewed/' + notificationId + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch();
    },

    enableLangifyFeature: (featureName, callback) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'shop/feature/enable/' + featureName + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            if(callback) {
                callback(data);
            }
        })
    },
    disableLangifyFeature: (featureName, callback) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'shop/feature/disable/' + featureName + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST'
        })
        .then(data => data.json())
        .then(data => {
            if(callback) {
                callback(data);
            }
        })
    },

    saveShopSetting: async (name, value) => {
        let result = await fetch(PROTOCOL + HOST + ENDPOINT + 'shop/settings?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify({
                "name": name,
                "value": value
            })
        });
        return await result.json();
    },
    deleteShopSetting: async (name) => {
        let result = await fetch(PROTOCOL + HOST + ENDPOINT + 'shop/settings/' + name + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'DELETE'
        })
        return await result.json();
    },



    // Partner App Integrations
    partnerGetAll: (response) => {
        fetch(`${PROTOCOL + HOST + ENDPOINT}partners?key=${KEY}`,
        {
            credentials: 'include',
            method: 'GET',
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    partnerGetIntegration: (partner, response) => {
        fetch(`${PROTOCOL + HOST + ENDPOINT}partner/${partner}?key=${KEY}`,
        {
            credentials: 'include',
            method: 'GET',
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    partnerSetApiKey: (partner, apiKey, response) => {
        fetch(`${PROTOCOL + HOST + ENDPOINT}partner/${partner}?key=${KEY}`,
        {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify({
                "api_key": apiKey
            })
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    partnerDeleteApiKey: (partner, response) => {
        fetch(`${PROTOCOL + HOST + ENDPOINT}partner/${partner}?key=${KEY}`,
        {
            credentials: 'include',
            method: 'DELETE'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },



    activatePartnerIntegration: async (partnerName, response) => {
        const res = await fetch(`${PROTOCOL + HOST + ENDPOINT}partner/${partnerName}?key=${KEY}`, {
            credentials: 'include',
            method: 'POST',
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },

    deletePartnerIntegration: async (partnerName, response) => {
        const res = await fetch(`${PROTOCOL + HOST + ENDPOINT}partner/${partnerName}?key=${KEY}`, {
            credentials: 'include',
            method: 'DELETE',
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },


    syncPartnerTexts : async (partnerName, languageId = '', response) => {
        const res = await fetch(`${PROTOCOL + HOST + ENDPOINT}partner/${partnerName}/sync/${languageId}?key=${KEY}`, {
            credentials: 'include',
            method: 'POST',
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },

    getPartnerTexts : async (partnerName, languageId, response) => {
        const res = await fetch(`${PROTOCOL + HOST + ENDPOINT}partner/${partnerName}/get/${languageId}?key=${KEY}`, {
          credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },

    savePartnerTexts: async (partnerName, languageId, data, response) => {
        const res = await fetch(`${PROTOCOL + HOST + ENDPOINT}partner/${partnerName}/save/${languageId}?key=${KEY}`, {
            method: 'POST',
            credentials: 'include',
            headers: {
            'Content-Type': 'application/json',
            },
            body: JSON.stringify({ data }),
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },

    updatePartnerTexts: async (partnerName, languageId, response) => {
        const res = await fetch(`${PROTOCOL + HOST + ENDPOINT}partner/${partnerName}/update/${languageId}?key=${KEY}`, {
            credentials: 'include',
            method: 'POST',
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },

    /**
     * MetaObjects
     * 
     * @Route("/v2/api/metaobjects/definitions/{perPage}", methods={"POST"})
     * @Route("/v2/api/metaobjects/search/{perPage}", methods={"POST"})
     * @Route("/v2/api/metaobject/{languageId}", methods={"POST"})
     * @Route("/v2/api/metaobject/save/{languageId}", methods={"POST"})
     */ 
    metaObjectsDefinitions: (filterObject = {}, response) => {
        let perPage = 25;
        fetch(PROTOCOL + HOST + ENDPOINT + 'metaobjects/definitions/' + perPage + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify(filterObject)
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    metaObjectsSearch: (filterObject, response) => {
        /*
{
                "definition_type": "",
                "search": {
                    "display_name" : "",
                    "updated_at" : ""
                },
                "sort_key": "updated_at"
            }
        */
        let perPage = 25;
        fetch(PROTOCOL + HOST + ENDPOINT + 'metaobjects/search/' + perPage + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify(filterObject)
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    metaObjectSync: (metaObjectId, languageId, response) => {
        _fetch(PROTOCOL + HOST + ENDPOINT + 'metaobject/' + languageId + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify({
                resource_id: 'gid://shopify/Metaobject/' + metaObjectId
            })
        }, {language: languageId})
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    metaObjectSave: async (languageId, items, progress, response) => {
        let itemNum = 0;
        let j = items.length;
        let fetches = [];
        for(let i = 0; i < items.length; i++) {
            const item = await _fetch(PROTOCOL + HOST + ENDPOINT + 'metaobject/save/' + languageId + '?key=' + KEY,
                {
                    credentials: 'include',
                    body: JSON.stringify(items[i]),
                    method: 'POST'
                }, {language: languageId})
                .then(data => {
                    itemNum = itemNum++;
                    progress(Math.round(itemNum / j * 100), items[i]);
                    return data.json()
                })
                .then(data => {
                    let appendix = {requestBody: items[i]};
                    return {...data, ...appendix}
                })
                .catch(e => e)

            // One metaobject per request, so aborting the one in flight does not end the loop.
            if(item && item.aborted === true) {
                response(item);
                return;
            }

            fetches.push(item)
        }

        Promise.all(fetches)
        .then(data => {
            progress(100, null);
            response(data);
        })
        
        .catch(error => response(error));
    },


    /**
     * Generic translatable resources (TranslatableResourcesController)
     *
     * @Route("/v2/api/translations/get_by_type/{resourceType}/{locale}/{perPage}", methods={"GET"})
     * @Route("/v2/api/translations/save", methods={"POST"})
     *
     * Works for any resourceType of Shopify's TranslatableResourceType enum (FILTER,
     * COOKIE_BANNER, SELLING_PLAN, MEDIA_IMAGE, ...). {locale} takes an iso code or the local
     * language id prefixed with "ly".
     */
    translatableResourcesByType: (resourceType, languageId, options = {}, response) => {
        const perPage = options.perPage ? options.perPage : 100; // the backend rejects more than 100
        let url = PROTOCOL + HOST + ENDPOINT + 'translations/get_by_type/' + resourceType + '/ly' + languageId + '/' + perPage + '?key=' + KEY;
        // a shopify cursor is base64 and needs encoding to survive the query string
        if(options.cursor) {
            url += '&cursor=' + encodeURIComponent(options.cursor);
        }
        if(options.outdated === true) {
            url += '&outdated=1';
        }
        // without this only resources that already have a source value come back
        if(options.includeEmptySource === true) {
            url += '&include_empty_source=1';
        }
        _fetch(url,
        {
            credentials: 'include',
            method: 'GET'
        }, {language: languageId})
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    // resources is a map of GID => {translations: [{key, target, digest}]}, an empty target deletes.
    // The route rejects more than 100 resources per direction, so a bigger map is sent in chunks
    // and the responses are merged back into one
    translatableResourcesSave: (languageId, resources, response) => {
        const gids = Object.keys(resources);
        const chunks = [];
        for(let i = 0; i < gids.length; i += 100) {
            chunks.push(gids.slice(i, i + 100).reduce((chunk, gid) => {
                chunk[gid] = resources[gid];
                return chunk;
            }, {}));
        }

        // The chunks go out together rather than one after another, so there is no loop to end -
        // the drop is caught here instead. Without this an aborted chunk reaches the merge below
        // with no `data`, and the result reads as a save that partly failed.
        Promise.all(chunks.map(chunk =>
            _fetch(PROTOCOL + HOST + ENDPOINT + 'translations/save?key=' + KEY,
            {
                credentials: 'include',
                method: 'POST',
                body: JSON.stringify({
                    locale: 'ly' + languageId,
                    translatable_resources: chunk
                })
            }, {language: languageId})
            .then(data => data.json())
        ))
        .then(results => {
            const aborted = results.find(result => result && result.aborted === true);
            if(aborted) {
                response(aborted);
                return;
            }
            if(results.length === 1) {
                response(results[0]);
                return;
            }
            response(results.reduce((merged, result) => ({
                success: merged.success && result.success === true,
                data: {
                    locale: result.data ? result.data.locale : merged.data.locale,
                    succeeded: merged.data.succeeded + (result.data ? result.data.succeeded : 0),
                    failed: merged.data.failed + (result.data ? result.data.failed : 0),
                    translatable_resources: {
                        ...merged.data.translatable_resources,
                        ...(result.data ? result.data.translatable_resources : {})
                    }
                }
            }), { success: true, data: { locale: null, succeeded: 0, failed: 0, translatable_resources: {} } }));
        })
        .catch(error => response(error));
    },


    accessUpdate: (response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'access/update?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST',
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },

    getMarkets: (response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'markets/get?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST',
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },


    screenLog: (body) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'bulk_translation/log?key=' + KEY, 
        {
            method: "POST",
            credentials: "include",
            body: JSON.stringify(body)
        })
        .then(data => data.json());
    },


    getPlans: (response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'plans?key=' + KEY,
        {
            credentials: 'include',
            method: 'GET',
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    changePlan: (newPlan, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'plan/' + newPlan + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST',
        })
        .then(data => data.json())
        .then(data => {
            //console.log(data)
            if(data.success === true && data.charge?.confirmation_url) {
                window.location.replace(data.charge.confirmation_url)
                return;
            }
            // no redirect: hand the answer back so the pending state is cleared (and e.g. plan_not_allowed for a discount code is shown)
            if(response) response(data);
        })
        .catch(error => { if(response) response(error); });
    },

    // Discount codes: the pending code is applied to the next plan charge (see GET plans -> discount)
    getDiscount: (response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'discount?key=' + KEY,
        {
            credentials: 'include',
            method: 'GET',
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    redeemDiscount: (code, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'discount/redeem?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify({ code: code }),
        })
        // 422 and 429 carry a JSON body with the error code as well
        .then(res => res.json().then(data => ({ ...data, status: res.status })))
        .then(data => {
            response(data);
        })
        .catch(error => response({ success: false, error: 'network', message: error && error.message }));
    },
    removeDiscount: (response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'discount?key=' + KEY,
        {
            credentials: 'include',
            method: 'DELETE',
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response({ success: false, error: 'network', message: error && error.message }));
    },

    
    freshdeskGetContact: (response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'freshdesk/contact?key=' + KEY,
        {
            credentials: 'include',
            method: 'GET',
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    freshdeskCreateContact: (firstname, lastname, email, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'freshdesk/contact?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify({
                firstname: firstname,
                lastname: lastname,
                email: email
            })
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    freshdeskGetAllTickets: (response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'freshdesk/tickets?key=' + KEY,
        {
            credentials: 'include',
            method: 'GET',
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    freshdeskGetTicket: (ticketId, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'freshdesk/tickets/' + ticketId + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'GET',
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    freshdeskCreateTicket: (formData, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'freshdesk/ticket?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST',
            header: {
                'Content-Type': 'multipart/form-data'
            },
            body: formData
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },
    freshdeskReplyTicket: (ticketId, formData, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'freshdesk/tickets/' + ticketId + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST',
            header: {
                'Content-Type': 'multipart/form-data'
            },
            body: formData
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },


    //@Route("/v2/api/available_words_log/{page}/{perPage}", methods={"POST"})
    getAvailableWordsLog: (page, perPage, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'available_words_log/' + page + '/' + perPage + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify({
                
            })
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },




    getAppEmbedsFromSettingsData: (themeId, response) => {
        // /v2/api/app_embed/get_app_embeds_from_settings_data/{themeId}
        // Declares the theme and no language: this one answers with the embeds of a theme, so a
        // theme switch invalidates it and a language switch does not.
        _fetch(PROTOCOL + HOST + ENDPOINT + 'app_embed/get_app_embeds_from_settings_data/' + themeId + '?key=' + KEY,
            {
                credentials: 'include'
            }, {theme: themeId})
            .then(data => data.json())
            .then(data => {
                response(data);
            })
        .catch(error => response(error));
    },
    // Declares the theme as well: the embed and its strings belong to one theme's
    // settings_data, so a theme switch invalidates this like a language switch does.
    getAppEmbed: (appEmbedId, languageId, themeId, response) => {
        let perPage = 100;
        // /v2/api/app_embed/get_app_embed/{languageId}
        _fetch(PROTOCOL + HOST + ENDPOINT + 'app_embed/get_app_embed/' + languageId + '?key=' + KEY,
            {
                credentials: 'include',
                method: 'POST',
                body: JSON.stringify({
                    gid: appEmbedId
                })
            }, {language: languageId, theme: themeId})
            .then(data => data.json())
            .then(data => {
                response(data);
            })
        .catch(error => response(error));
    },
    saveAppEmbeds: async(languageId, themeId, items, progress, response) => {
        let j = items.length
        let fetches = []
        for(let i = 0; i < j; i++) {
            // /v2/api/app_embed/save/{languageId}
            const item = await _fetch(PROTOCOL + HOST + ENDPOINT + 'app_embed/save/' + languageId + '?key=' + KEY,
                    {
                        credentials: 'include',
                        method: 'POST',
                        body: JSON.stringify(items[i])
                    }, {language: languageId, theme: themeId})
                    .then(data => {
                        progress(Math.round(i / j * 100))
                        return data.json()
                    })
                    .then(data => {
                        let appendix = {requestBody: items[i]};
                        return {...data, ...appendix}
                    })
                    .catch(e => e)

            // One embed per request, so aborting the one in flight does not end the loop.
            if(item && item.aborted === true) {
                response(item);
                return;
            }

            fetches.push(item)
        }


        Promise.allSettled([
            ...fetches
        ])
        .then(data => {
            progress(100)
            response(data)
        })
        .catch(error => response(error))
    },


    sendAppFeedback: (feedback, response)  => {
        const options = {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          feedback: feedback
        })
      };
      fetch(PROTOCOL + HOST + ENDPOINT + 'langify/feedback?key=' + KEY, options)
        .then(response => response.json())
        .then((response) => {
            response();
        })
        .catch((err) => {
            response();
            console.error(err)
        });
    },

    // GLOSSARY API FUNCTIONS
    getGlossaryJWT: (response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'glossary/jwt?key=' + KEY, {
            credentials: 'include',
            method: 'GET'
        })
        .then(data => data.json())
        .then(data => response(data))
        .catch(error => response(error));
    },

    getGlossary: (languageId, response) => {
        const url = PROTOCOL + HOST + ENDPOINT + 'glossary/language/' + languageId + '?key=' + KEY;
        fetch(url, {
            credentials: 'include',
            method: 'GET'
        })
        .then(data => data.json())
        .then(data => {console.log(data); return response(data);})
        .catch(error => response(error));
    },

    createGlossaryEntry: (source, type, target, languageId, response) => {
        const url = PROTOCOL + HOST + ENDPOINT + 'glossary?key=' + KEY;
        fetch(url, {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify({
                source: source,
                type: type,
                target: target,
                languageId: languageId
            })
        })
        .then(data => data.json())
        .then(data => response(data))
        .catch(error => response(error));
    },

    deleteGlossaryEntry: (glossaryTextId, language, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'glossary/' + glossaryTextId + '/' + language + '?key=' + KEY, {
            credentials: 'include',
            method: 'DELETE'
        })
        .then(data => data.json())
        .then(data => response(data))
        .catch(error => response(error));
    },

    updateGlossaryEntry: (entryId, source, type, target, language, response) => {
        if(target === '') {
            const url = PROTOCOL + HOST + ENDPOINT + 'glossary/' + entryId + '/translation/' + language + '?key=' + KEY;
            fetch(url, {
                credentials: 'include',
                method: 'DELETE',
            })
            .then(data => data.json())
            .then(data => response(data))
            .catch(error => response(error));
        } else {
            const url = PROTOCOL + HOST + ENDPOINT + 'glossary?key=' + KEY;
            fetch(url, {
                credentials: 'include',
                method: 'POST',
                body: JSON.stringify({
                    id: entryId,
                    source: source,
                    type: type,
                    target: target,
                    languageId: language
                })
            })
            .then(data => data.json())
            .then(data => response(data))
            .catch(error => response(error));
        }
    },

    // a mode switch only: posts the type, never the empty-target DELETE branch above - the
    // translations stay and show again when the term goes back to translate
    updateGlossaryEntryMode: (entryId, source, type, languageId, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'glossary?key=' + KEY, {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify({
                id: entryId,
                source: source,
                type: type,
                target: '',
                languageId: languageId
            })
        })
        .then(data => data.json())
        .then(data => response(data))
        .catch(error => response(error));
    },

    createGlossaryTranslation: (glossaryTextId, language, translation, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'glossary/translation?key=' + KEY, {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify({
                glossary_text_id: glossaryTextId,
                language: language,
                translation: translation
            })
        })
        .then(data => data.json())
        .then(data => response(data))
        .catch(error => response(error));
    },

    deleteGlossaryTranslation: (entryId, language, response) => {
        const url = PROTOCOL + HOST + ENDPOINT + 'glossary/' + entryId + '/translation/' + language + '?key=' + KEY;
        fetch(url, {
            credentials: 'include',
            method: 'DELETE',
        })
        .then(data => data.json())
        .then(data => response(data))
        .catch(error => response(error));
    },

    getGlossaryExport: (response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'glossary/export?key=' + KEY, {
            credentials: 'include',
            method: 'GET'
        })
        .then(data => data.json())
        .then(data => response(data))
        .catch(error => response(error));
    },

    exportGlossary: (jwt, response) => {
        fetch(PROTOCOL + HOST + ENDPOINT + 'glossary/export?key=' + KEY, {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify({
                jwt: jwt
            })
        })
        .then(data => data.json())
        .then(data => response(data))
        .catch(error => response(error));
    },

    importGlossary: (jwt, file, response) => {
        const formData = new FormData();
        formData.append('jwt', jwt);
        formData.append('file', file);
        fetch(PROTOCOL + HOST + ENDPOINT + 'glossary/import?key=' + KEY, {
            credentials: 'include',
            method: 'POST',
            body: formData
        })
        .then(data => data.json())
        .then(data => response(data))
        .catch(error => response(error));
    },

    getConfigDefault: (callback) => {
        _fetch(PROTOCOL + HOST + ENDPOINT + 'settings/key/filter/default?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            callback(data);
        })
        .catch(error => callback(error));
    },

    getConfig: (...args) => {
        const response = args[args.length - 1];
        const endpoint = 'settings/key/filter';
        _fetch(PROTOCOL + HOST + ENDPOINT + endpoint + '?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },

    saveConfig: (data, callback) => {
        _fetch(PROTOCOL + HOST + ENDPOINT + 'settings/key/filter/save?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        })
        .then(data => data.json())
        .then(data => {
            callback(data);
        })
        .catch(error => callback(error));
    },

    deleteConfig: (...args) => {
        const response = args[args.length - 1];
        const pathSegments = args.slice(0, -1);
        const path = pathSegments.filter(seg => seg != null && seg !== '').join('/');
        const endpoint = path ? `settings/${path}` : 'settings';
        _fetch(PROTOCOL + HOST + ENDPOINT + endpoint + '?key=' + KEY,
        {
            credentials: 'include',
            method: 'DELETE'
        })
        .then(data => data.json())
        .then(data => {
            response(data);
        })
        .catch(error => response(error));
    },

    getDeeplSettings: (callback) => {
        _fetch(PROTOCOL + HOST + ENDPOINT + 'settings/deepl?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            callback(data);
        })
        .catch(error => callback(error));
    },

    saveDeeplSettings: (data, callback) => {
        _fetch(PROTOCOL + HOST + ENDPOINT + 'settings/deepl/save?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        })
        .then(data => data.json())
        .then(data => {
            callback(data);
        })
        .catch(error => callback(error));
    },

    getTranslationInstructions: (callback) => {
        _fetch(PROTOCOL + HOST + ENDPOINT + 'settings/translation_instructions?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            callback(data);
        })
        .catch(error => callback(error));
    },

    getDeeplSettingsDefault: (callback) => {
        _fetch(PROTOCOL + HOST + ENDPOINT + 'settings/deepl/default?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            callback(data);
        })
        .catch(error => callback(error));
    },

    getGlossarySettings: (callback) => {
        _fetch(PROTOCOL + HOST + ENDPOINT + 'settings/glossary?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            callback(data);
        })
        .catch(error => callback(error));
    },

    saveGlossarySettings: (data, callback) => {
        _fetch(PROTOCOL + HOST + ENDPOINT + 'settings/glossary/save?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        })
        .then(data => data.json())
        .then(data => {
            callback(data);
        })
        .catch(error => callback(error));
    },

    getGlossarySettingsDefault: (callback) => {
        _fetch(PROTOCOL + HOST + ENDPOINT + 'settings/glossary/default?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            callback(data);
        })
        .catch(error => callback(error));
    },

    // whole-store item counts for the named sections, e.g. ['products', 'collections']
    getSectionCounts: (sections, callback) => {
        _fetch(PROTOCOL + HOST + ENDPOINT + 'counts?sections=' + sections.join(',') + '&key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => callback(data))
        .catch(error => callback(error));
    },

    getBulkSettings: (callback) => {
        _fetch(PROTOCOL + HOST + ENDPOINT + 'settings/bulk?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            callback(data);
        })
        .catch(error => callback(error));
    },

    saveBulkSettings: (data, callback) => {
        _fetch(PROTOCOL + HOST + ENDPOINT + 'settings/bulk/save?key=' + KEY,
        {
            credentials: 'include',
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        })
        .then(data => data.json())
        .then(data => {
            callback(data);
        })
        .catch(error => callback(error));
    },

    getBulkSettingsDefault: (callback) => {
        _fetch(PROTOCOL + HOST + ENDPOINT + 'settings/bulk/default?key=' + KEY,
        {
            credentials: 'include'
        })
        .then(data => data.json())
        .then(data => {
            callback(data);
        })
        .catch(error => callback(error));
    },
 };

export default API